<script lang="ts">
  import { onMount, onDestroy, tick } from 'svelte';
  import { agentName as agentNameStore, userEmail, georgeDiscussContext, chatSeed, replyDraftSeed, pollVersion, currentView, lastTopLevelView, avatarColor, initialsFor, clientContactSeed, stages, stageColor, stageLabel, terms, setClientStage } from '../lib/stores';
  import { renderSafeMarkdown, unwrapHardBreaks, repairLostApostrophes, stripCids } from '../lib/markdown';
  import { formatFileSize, parseDay } from '../lib/format';
  import { followupStatus } from '../lib/followup';
  import MultiSelect from '../lib/MultiSelect.svelte';
  import EmailBodyEditor from '../lib/EmailBodyEditor.svelte';
  import { createSplitRail } from '../lib/splitRail.svelte';
  import { isSupportView, downloadSupportFile } from '../lib/supportView';
  import {
    listFiles, readFileContent, getThreadMeta, getDocumentUrl,
    getClientContacts, getInternalContacts, getContacts,
    generateEmail, saveDraft, polishEmail, chat as chatApi, saveInsights,
    getOutreachRecommendations, markOutreachSent,
    getPersonas, setActivePersona,
    rebuildClient, rebuildClientStatus,
    getFollowup, setFollowup, clearFollowup,
    getClientCalls, readCall, readTranscript, deleteCall, saveFileContent,
    type FileEntry, type OutreachEntry, type PersonasData, type CallEntry, type Contact,
  } from '../lib/api';
  import ScoreChart from '../lib/ScoreChart.svelte';

  let { client }: { client: string } = $props();
  const viewStorage = isSupportView() ? sessionStorage : localStorage;
  const clientStage = $derived($stages[client] || '');

  let activeTab = $state<'threads' | 'calls' | 'documents' | 'notes' | 'chat' | 'insights'>('chat');

  // Dev-only affordances (Strategize chatMode toggle) — same gate as Admin.svelte's isDev.
  const isDev = $derived($userEmail.toLowerCase().endsWith('@dealton.ai'));

  // Split-workspace layout (strat-split): George is a persistent left rail (keeps the
  // activeTab='chat' seed logic), the right "records" pane switches via mainSeg.
  let mainSeg = $state<'george' | 'threads' | 'calls' | 'documents' | 'notes' | 'people' | 'insights'>('threads');

  // People / contacts segment — reuse the rich Contact records (getContacts) +
  // the Contacts page's avatar helpers, filtered to this client.
  let clientContacts = $state<Contact[]>([]);
  let contactsReady = $state(false);
  async function loadContacts(c?: string) {
    const cl = c || client;
    try {
      const [all] = await Promise.all([getContacts(cl), loadEmails(cl)]);
      if (cl !== client) return;
      const emailSet = new Set(clientEmails.map((e) => e.toLowerCase()));
      clientContacts = all.filter((x) => x.client === cl || emailSet.has((x.email || '').toLowerCase()));
    } catch { if (cl === client) clientContacts = []; }
    finally { if (cl === client) contactsReady = true; }
  }
  /* Arriving from Contacts' per-row `Open`: land on the People tab with that person expanded,
     rather than on Emails with no sign of who was clicked. Cleared once consumed so a later
     visit to the same client opens normally. The client is checked because the seed outlives
     the navigation by a tick and must not fire on a different client. */
  $effect(() => {
    const seed = $clientContactSeed;
    if (!seed || seed.client !== client) return;
    $clientContactSeed = null;
    mainSeg = 'people';                 // drives loadContacts() via the effect above
    expandedContact = seed.email;
    // After the pane renders. Same shape as `openThreadInEmails`.
    setTimeout(() => {
      document.querySelector(`[data-contact="${CSS.escape(seed.email)}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
  });

  function writeToContact(email: string) {
    mainSeg = 'threads';
    chatDraftThread = '__new';
    chatDraftTo = email;
    chatDraftOpen = true;
  }
  let expandedContact = $state('');
  function openThreadInEmails(f: FileEntry) {
    mainSeg = 'threads';
    emailQuery = '';  // make sure the target thread isn't filtered out of the list
    if (!expandedThreads.includes(f.name)) { expandedThreads = [...expandedThreads, f.name]; loadThreadContent(f); }
    setTimeout(() => {
      document.querySelector(`[data-thread="${CSS.escape(f.name)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  }
  // Suggested-action chips above the Ask box — send the text straight to George.
  function quickAsk(t: string) { chatInput = t; handleChat(); }
  // Once George has replied, the suggested row swaps starter prompts for concrete
  // actions: Draft email (until one's been drafted) + Save to memory.
  let draftedThisSession = $state(false);
  function draftFromChat() {
    const last = [...messages].reverse().find((m) => m.role === 'assistant');
    openChatDraft(last?.content);
  }
  function restartConversation() {
    if (messages.length && !confirm('Restart the conversation? This clears the current chat.')) return;
    messages = []; chatDraftOpen = false; lastSavedSession = ''; draftedThisSession = false;
  }
  // A thread's display title. Prefer the REAL Subject header; fall back to
  // de-slugging the filename.
  //
  // The filename is a slug, not a title: the poller's `slugify()` lowercases it,
  // strips punctuation and hard-cuts at 50 chars — which is what turned
  // "Updated invitation: GeoMetAI <> Porgera @ Wed May 13, 2026 7pm" into
  // "Updated invitation geometai porgera wed may 13 202" (note the severed year
  // and the flattened "GeoMetAI"). Capitalising character 0 can't undo any of
  // that, so use `subject` (= thread_index.last_subject) whenever we have it.
  //
  // We can't drop the fallback: `last_subject` postdates most ingests, so only
  // ~1 row in 13 carries one today. Threads ingested since then look right;
  // older ones keep the de-slugged name until they're re-polled.
  function prettyThreadName(f: FileEntry): string {
    const subj = cleanSubject(f.subject);
    if (subj) return subj;
    let s = (f.clean_name || f.stem || f.name || '').replace(/\.md$/, '');
    s = s.replace(/^\d{4}-\d{2}-\d{2}[_-]?/, '').replace(/[_-]+/g, ' ').trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : (f.clean_name || f.stem || f.name);
  }
  // MIME headers fold across lines (RFC 5322 §2.2.3), so a stored subject can
  // carry "\n " mid-value — unfold it. Then strip the Re:/Fwd: chain: a thread's
  // identity is its subject, and the chain is per-message noise. "Accepted:" /
  // "Invitation:" deliberately survive — they say what the message IS.
  function cleanSubject(raw?: string): string {
    if (!raw) return '';
    return raw
      .replace(/\s+/g, ' ')
      .replace(/^(?:(?:re|fw|fwd|aw|sv)\s*(?:\[\d+\])?\s*:\s*)+/i, '')
      .trim();
  }
  function prettyDocName(f: FileEntry): string {
    const raw = (f.clean_name || f.name || '').replace(/\.(pdf|md|docx?|xlsx?|pptx?|txt|csv|html?)$/i, '');
    const t = raw.replace(/[_-]+/g, ' ').trim();
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : (f.clean_name || f.name);
  }
  function threadDate(f: FileEntry): string {
    const m = (f.stem || f.name || '').match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : '';
  }
  // Parse calendar days in the viewer's timezone, not at UTC midnight.
  function daysAgo(dateStr?: string): string {
    if (!dateStr) return '';
    const d = parseDay(dateStr);
    if (isNaN(d.getTime())) return '';
    const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
    return diff <= 0 ? 'today' : diff === 1 ? '1d ago' : `${diff}d ago`;
  }
  function fmtShortDate(dateStr?: string): string {
    if (!dateStr) return '';
    const d = parseDay(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  // Company-level follow-up is the same dashboard_cache value the Dashboard
  // uses. Old thread dates remain visible on their threads, but do not make the
  // whole account overdue after a newer conversation changes the next step.
  let companyFollowup = $state<string | null>(null);
  const clientFollowup = $derived(companyFollowup === null ? null : followupStatus(companyFollowup, clientStage));
  // Emails: multiple threads expandable at once, each with its own loaded content.
  let expandedThreads = $state<string[]>([]);
  let threadContent = $state<Record<string, string>>({});
  let threadLoading = $state<Record<string, boolean>>({});
  let threadError = $state<Record<string, boolean>>({});
  async function loadThreadContent(f: FileEntry) {
    if (threadContent[f.name] !== undefined) return;
    threadLoading = { ...threadLoading, [f.name]: true };
    threadError = { ...threadError, [f.name]: false };
    try {
      const r = await readFileContent(client, folder, f.name);
      threadContent = { ...threadContent, [f.name]: r.content };
    } catch {
      // Do NOT cache '' here — with the `!== undefined` guard
      // above then treated the failure as a loaded-and-empty thread, ONE failed
      // fetch made the thread permanently unreadable for the rest of the page
      // session — and it fell through to the summary branch, so a 44-message
      // conversation rendered as its own AI summary with no hint anything broke.
      // (Reproduces every time the backend hot-reloads while the page is open.)
      // Leaving the key unset lets the next expand retry.
      threadError = { ...threadError, [f.name]: true };
    }
    threadLoading = { ...threadLoading, [f.name]: false };
  }
  function toggleThread(f: FileEntry) {
    if (expandedThreads.includes(f.name)) expandedThreads = expandedThreads.filter((n) => n !== f.name);
    else { expandedThreads = [...expandedThreads, f.name]; loadThreadContent(f); }
  }
  async function replyToThread(f: FileEntry) {
    const stem = f.stem || f.name.replace(/\.md$/, '');
    if (chatDraftOpen && chatDraftThread === stem) return;  // already drafting this thread
    if (!canOpenDraft()) return;
    if (!expandedThreads.includes(f.name)) expandedThreads = [...expandedThreads, f.name];
    // One body read for the whole flow. loadThreadContent caches, so re-opening the
    // same thread costs nothing; nothing here may re-read it for a copy it discards.
    await loadThreadContent(f);   // guarded: reads once if uncached, 0 if already open
    selectThread(f);
    const body = threadContent[f.name] || '';
    chatDraftContext = body ? `## Email Thread\n${body.slice(0, 4000)}` : '';
    chatDraftMeta = await getThreadMeta(client, stem, selectedThreadId);
    chatDraftThread = stem;
    chatDraftTo = chatDraftMeta.default_to;
    chatDraftSubject = chatDraftMeta.default_subject;
    chatDraftBody = ''; chatDraftInstruction = '';
    chatDraftCcClient = chatDraftMeta.cc_participants || [];
    chatDraftCcInternal = chatDraftMeta.cc_internal || []; chatDraftCcOther = '';
    resetAttachments();
    chatDraftOpen = true;
  }
  // Strip the metadata header + summary; show just the messages (design's message list).
  function threadMessagesOnly(content: string): string {
    if (!content) return '';
    const sep = content.indexOf('\n---\n');
    if (sep >= 0) return content.slice(sep + 5).trim();
    const m = content.match(/\n### /);
    return (m && m.index !== undefined) ? content.slice(m.index + 1).trim() : content;
  }
  // Parse "### DATE TIME — Direction — Sender <email>\nbody" blocks into message rows.
  /* A thread header is `### <date> — <direction> — <From>`, and `<From>` is sometimes a bare
     `<addr@x.com>` with no display name. Stripping the angle-bracket address then left an
     EMPTY string, which the row rendered as an em dash — so a run of outbound messages showed
     no sender at all. Fall back to the address, prettified the same way the backend derives a
     sign-off from one (`email_generation.py:579`): local part, dots to spaces, title case.
     That is a presentation rule the app already applies, not invented data — the address is
     right there in the header. */
  function senderName(raw: string): string {
    const named = raw.replace(/\s*<[^>]+>\s*/, '').replace(/^["']|["']$/g, '').trim();
    if (named) return named;
    const addr = (raw.match(/[\w.+-]+@[\w.-]+/) || [''])[0];
    if (!addr) return '';
    return addr.split('@')[0].replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function parseThreadMessages(content: string): { sender: string; date: string; direction: string; body: string }[] {
    const msgs = threadMessagesOnly(content);
    if (!msgs) return [];
    const out: { sender: string; date: string; direction: string; body: string }[] = [];
    for (const block of msgs.split(/\n(?=###\s)/)) {
      const nl = block.indexOf('\n');
      const header = (nl >= 0 ? block.slice(0, nl) : block).replace(/^###\s*/, '').trim();
      const body = nl >= 0 ? block.slice(nl + 1).trim() : '';
      const m = header.match(/^(\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2})\s*—\s*([^—]+?)\s*—\s*(.+)$/);
      if (m) out.push({ date: m[1].trim(), direction: m[2].trim(), sender: senderName(m[3]), body });
      else if (header || body) out.push({ date: '', direction: '', sender: header, body });
    }
    return out;
  }
  // Per-message collapse — latest message open by default; explicit toggles override.
  let expandedMsgs = $state<Record<string, boolean>>({});
  function isMsgOpen(fname: string, i: number, total: number): boolean {
    const k = fname + '::' + i;
    return (k in expandedMsgs) ? expandedMsgs[k] : (i === total - 1);
  }
  function toggleMsg(fname: string, i: number, total: number) {
    const k = fname + '::' + i;
    const cur = (k in expandedMsgs) ? expandedMsgs[k] : (i === total - 1);
    expandedMsgs = { ...expandedMsgs, [k]: !cur };
  }
  function msgSnippet(body: string): string {
    return repairLostApostrophes(stripCids(body || '')).replace(/[#*>`_~-]/g, '').replace(/\s+/g, ' ').trim().slice(0, 140);
  }
  // Drafts live in exactly ONE editor at a time (the new-email slot at the top,
  // or a thread's Respond slot). You overwrite/rewrite it, then resolve it by
  // Send / Schedule / Discard — there's no "save a pile of drafts" path.
  function draftHasContent(): boolean {
    return !!(chatDraftBody.trim() || chatDraftInstruction.trim());
  }
  function discardDraft() {
    if (draftHasContent() && !confirm('Discard this draft? It hasn\'t been sent.')) return;
    chatDraftOpen = false;
    resetChatDraft();
  }
  // Guard opening a second slot while an unsent draft is in progress.
  function canOpenDraft(): boolean {
    if (chatDraftOpen && draftHasContent()) {
      return confirm('You have an unfinished draft. Discard it and start a new one?');
    }
    return true;
  }
  function startNewEmail() {
    if (chatDraftOpen && chatDraftThread === '__new') return;  // the new-email slot is already open
    if (!canOpenDraft()) return;
    resetChatDraft(); chatDraftThread = '__new'; chatDraftOpen = true;
  }
  // Warn on refresh / tab-close while a draft is unsent (mirror the in-app guard).
  $effect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (chatDraftOpen && draftHasContent()) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  });
  // George is a persistent rail on wide screens; when the rail is collapsed OR the
  // screen is narrow it drops into the tab bar as a peer of Emails/Contacts.
  let shellEl = $state<HTMLElement>();
  const rail = createSplitRail({
    keyW: 'cvRailW', keyCollapsed: 'cvRailCollapsed', getShell: () => shellEl,
  });
  // When the rail becomes visible again, don't leave the (now-hidden) George tab active.
  $effect(() => { if (!rail.showTab && mainSeg === 'george') mainSeg = 'threads'; });
  // Threads
  let folder = $state('emails');
  let files = $state<FileEntry[]>([]);
  let emailsReady = $state(false);
  let emailsError = $state('');
  let emailQuery = $state('');
  const filteredEmailFiles = $derived(
    emailQuery.trim() === ''
      ? files
      : files.filter((f) => (prettyThreadName(f) + ' ' + (f.stem || '') + ' ' + (f.clean_name || '')).toLowerCase().includes(emailQuery.toLowerCase().trim()))
  );
  // The only consumed piece of the old file-selection state: `getThreadMeta` takes it
  // as `tid`. Threads render from `expandedThreads` + `threadContent`, so the former
  // `selectedFile` / `fileContent` / `loadingFile` had no reader.
  let selectedThreadId = $state('');

  // Reply compose state


  // Chat inline compose state
  let chatDraftOpen = $state(false);
  let chatDraftTo = $state('');
  let chatDraftToManual = $state('');
  let chatDraftSubject = $state('');
  let chatDraftBody = $state('');
  let chatDraftNotes = $state('');
  let chatDraftCcClient = $state<string[]>([]);
  let chatDraftCcInternal = $state<string[]>([]);
  let chatDraftCcOther = $state('');
  let chatDraftInstruction = $state('');
  let chatDraftContext = $state('');
  // Type-specific brief carried in via replyDraftSeed (LIGHT/HEAVY overdue,
  // COLD, MEETING_FOLLOWUP, prospecting, inbound reply). Prepended to the
  // user's chatDraftInstruction when Generate runs, so a regen after Discuss
  // keeps the same prompt the original draft was written under.
  let chatDraftBrief = $state('');
  let chatDraftThread = $state('');  // '' = new email, or thread stem for reply
  let chatDraftMeta = $state<{ default_to: string; default_subject: string; gmail_thread_id: string; cc_participants?: string[]; cc_internal?: string[] }>({ default_to: '', default_subject: '', gmail_thread_id: '', cc_participants: [], cc_internal: [] });
  // Which visible thread the open draft targets (renders the composer inside it; else at top).
  const draftThreadFile = $derived(
    chatDraftOpen && chatDraftThread && chatDraftThread !== '__new'
      ? files.find((x) => (x.stem || x.name.replace(/\.md$/, '')) === chatDraftThread) || null
      : null
  );
  // The composer rendered at the TOP of Emails (new email / AI-decides / a reply whose
  // thread isn't expanded). While it's up, the "+ New email" button is redundant.
  const topComposerOpen = $derived(
    chatDraftOpen && (chatDraftThread === '__new' || chatDraftThread === '' || !draftThreadFile || !expandedThreads.includes(draftThreadFile.name))
  );

  // Attachments — shared across all compose surfaces
  let outreachDocs = $state<OutreachEntry[]>([]);
  let selectedOutreachIds = $state<string[]>([]);
  let customFiles = $state<File[]>([]);

  // Shared
  let generating = $state(false);
  let saving = $state(false);
  let clientEmails = $state<string[]>([]);
  let internalEmails = $state<string[]>([]);

  // Chat — `hidden` messages are sent to George but not rendered (e.g. the
  // Re-engage seed prompt: the user sees only the response).
  let messages = $state<{ role: string; content: string; hidden?: boolean }[]>([]);
  let chatInput = $state('');
  let chatLoading = $state(false);
  let refThread = $state('');
  let chatMode = $state<'cro' | 'george' | 'user'>((viewStorage.getItem('chatMode') as any) || 'cro');
  $effect(() => { viewStorage.setItem('chatMode', chatMode); });
  let emailThreads = $state<FileEntry[]>([]);

  // Documents
  let docFiles = $state<FileEntry[]>([]);
  let docSelected = $state('');
  let docPreviewUrl = $state('');
  let docPreviewError = $state(false);
  onDestroy(() => { if (docPreviewUrl) URL.revokeObjectURL(docPreviewUrl); });

  // Meetings (call history)
  let callEntries = $state<CallEntry[]>([]);
  let callsReady = $state(false);
  let expandedCall = $state('');
  let callContent = $state('');
  let editingCall = $state(false);
  let editCallText = $state('');
  let callTranscript = $state('');
  let loadingCall = $state(false);
  let showTranscript = $state(false);

  let toast = $state('');
  let toastError = $state(false);
  let lastSentNotice = $state('');

  // Persona
  let personasData = $state<PersonasData | null>(null);
  let selectedPersona = $state(viewStorage.getItem('selectedPersona') || '');

  // Polish
  let polishing = $state(false);
  let polishChanges = $state('');

  // Rebuild (destructive, tucked away in the Emails-tab footer)
  let rebuilding = $state(false);
  let rebuildOpen = $state(false);
  let rebuildConfirming = $state(false);
  let rebuildEmails = $state(true);
  let rebuildStrategy = $state(false);
  let rebuildContacts = $state(false);

  async function runRebuild() {
    rebuildOpen = false;
    rebuildConfirming = false;
    rebuilding = true;
    try {
      await rebuildClient(client, { emails: rebuildEmails, strategy: rebuildStrategy, contacts: rebuildContacts });
      const check = setInterval(async () => {
        const s = await rebuildClientStatus(client);
        if (!s.running) {
          clearInterval(check);
          rebuilding = false;
          loadEmails(client, true);
          // Rebuild touches data other pages display (Today's Respond bucket
          // among them — inbound drafts are wiped + regenerated). Bump the
          // shared poll counter so any subscriber (Today, Dashboard …) reloads
          // instead of sitting on a stale snapshot.
          pollVersion.update(v => v + 1);
        }
      }, 3000);
    } catch (e) {
      rebuilding = false;
      alert('Rebuild failed: ' + (e as Error).message);
    }
  }

  // Follow-up override
  let followupOverride = $state<{ date: string; reason: string; set_by: string } | null>(null);
  let followupDate = $state('');
  let followupReason = $state('');

  $effect(() => {
    const c = client;
    // Keep activeTab when switching clients (don't reset to 'threads')
    folder = 'emails';
    files = [];
    emailThreads = [];
    clientEmails = [];
    internalEmails = [];
    emailsReady = false;
    emailsError = '';
    emailsLoad = null;
    clientContacts = [];
    contactsReady = false;
    callEntries = [];
    callsReady = false;
    noteFiles = [];
    notesReady = false;
    docsReady = false;
    followupOverride = null;
    companyFollowup = null;
    followupDate = '';
    followupReason = '';
    selectedThreadId = '';
    messages = [];
    draftedThisSession = false;
    docFiles = [];
    docSelected = '';
    outreachDocs = [];
    outreachReady = false;
    chatDraftOpen = false;
    // If there's a pending $replyDraftSeed targeted at THIS client (e.g.
    // we just landed here from Today's Discuss button), don't wipe the
    // chat draft — the seed effect will populate it. Resetting here
    // races the seed effect's selectChatThread call and leaves the
    // Thread dropdown stuck on "AI decides" when the reset wins.
    const pendingSeed = $replyDraftSeed;
    if (!pendingSeed || pendingSeed.client !== c) {
      resetChatDraft();
    }
    resetAttachments();

    loadEmails(c);
    loadFollowup(c);
    loadPersonas();
  });

  // A teammate or a poll can move the company-level date while this page is
  // open. Revalidate on return to the tab and after a completed poll.
  onMount(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') loadFollowup(client); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  });
  $effect(() => { if ($pollVersion > 0) loadFollowup(client); });

  // Fetch each secondary segment when it is opened. Header status loads with
  // the client, independently of the email list.
  $effect(() => {
    const c = client;
    if (mainSeg === 'people') { loadContacts(c); loadCalls(c); }
    else if (mainSeg === 'notes') loadNotes(c);
    else if (mainSeg === 'documents') loadDocuments(c);
    // Follow-up loads with the client header, regardless of the open segment.
  });

  $effect(() => {
    if (chatDraftOpen && !outreachReady) loadOutreach(client);
  });

  // Handle George discuss context — tick() ensures this runs after the client-change effect resets messages
  $effect(() => {
    const ctx = $georgeDiscussContext;
    if (ctx) {
      $georgeDiscussContext = '';
      tick().then(() => {
        activeTab = 'chat';
        messages = [{ role: 'assistant', content: ctx }];
      });
    }
  });

  // Chat seed (e.g. the Re-engage button) — inject a hidden problem-statement
  // prompt and auto-generate George's answer on load. The user sees a small
  // italic header + the response streaming in, never the raw prompt.
  $effect(() => {
    const seed = $chatSeed;
    if (seed) {
      $chatSeed = null;
      tick().then(async () => {
        activeTab = 'chat';
        messages = [
          { role: 'system', content: seed.label },
          { role: 'user', content: seed.prompt, hidden: true },
        ];
        await processChat();
      });
    }
  });

  // Reply-draft seed (Today's Respond bucket "Discuss" button). The chat
  // seed effect above lands the user on the Strategize tab; this effect
  // pre-populates the in-tab CHAT DRAFT composer (not the per-thread
  // reply composer in the Emails tab) with the proposed To/CC/Subject/
  // Body. Everything lives on one surface: strategy chat above, the
  // draft email below. selectChatThread() with preserveFields=true sets
  // the thread reference so subject + threading metadata are correct
  // without clobbering the values we override from the seed.
  $effect(() => {
    const seed = $replyDraftSeed;
    if (!seed || seed.client !== client) return;
    $replyDraftSeed = null;
    tick().then(async () => {
      folder = 'emails';
      // The shared email load also includes contacts, so the thread selection
      // and CC rebucketing below see one consistent result.
      await loadEmails(client);

      // Rebucket the comma-joined seed.ccOther against the loaded contact
      // lists. Today doesn't have these lists so it dumps everything into
      // ccOther; we sort here. Case-insensitive match. Anything that doesn't
      // match either list (BCC contacts, one-offs) stays in ccOther.
      const lcClient = new Set(clientEmails.map(e => e.toLowerCase()));
      const lcInternal = new Set(internalEmails.map(e => e.toLowerCase()));
      const ccTokens = [
        ...seed.ccOther.split(','),
        ...(seed.ccClient || []),
        ...(seed.ccInternal || []),
      ].map(s => s.trim()).filter(Boolean);
      const bucketedClient: string[] = [];
      const bucketedInternal: string[] = [];
      const bucketedOther: string[] = [];
      for (const addr of ccTokens) {
        const lc = addr.toLowerCase();
        if (lcClient.has(lc)) bucketedClient.push(addr);
        else if (lcInternal.has(lc)) bucketedInternal.push(addr);
        else bucketedOther.push(addr);
      }

      chatDraftTo = seed.to;
      chatDraftSubject = seed.subject;
      chatDraftBody = seed.body;
      chatDraftCcClient = bucketedClient;
      chatDraftCcInternal = bucketedInternal;
      chatDraftCcOther = bucketedOther.join(', ');
      chatDraftBrief = seed.brief || '';
      chatDraftOpen = true;

      // Link the thread. inbound_drafts + proactive_drafts both store the
      // CANONICAL thread_id; FileEntry exposes both canonical and the
      // user-mapped one — try canonical first, then user-mapped. No match
      // (or empty threadId — prospecting, meeting_followup, going_cold)
      // means this is a NEW email, so default the dropdown to "New email"
      // rather than leaving it stuck on "AI decides".
      const f = seed.threadId
        ? emailThreads.find(t =>
            t.canonical_thread_id === seed.threadId ||
            t.thread_id === seed.threadId,
          )
        : undefined;
      if (f) {
        selectThread(f);
        await selectChatThread(f.stem, /* preserveFields */ true);
      } else {
        await selectChatThread('__new', /* preserveFields */ true);
      }
    });
  });

  function resetAttachments() {
    selectedOutreachIds = [];
    customFiles = [];
  }


  function resetChatDraft() {
    chatDraftTo = ''; chatDraftToManual = ''; chatDraftSubject = ''; chatDraftBody = '';
    chatDraftCcClient = []; chatDraftCcInternal = []; chatDraftCcOther = '';
    chatDraftInstruction = ''; chatDraftContext = ''; chatDraftBrief = '';
  }

  let emailsLoad: { client: string; promise: Promise<void> } | null = null;
  let emailsLoadSeq = 0;
  function loadEmails(c?: string, force = false): Promise<void> {
    const cl = c || client;
    if (!force && emailsLoad?.client === cl) return emailsLoad.promise;
    const seq = ++emailsLoadSeq;
    if (!force) emailsReady = false;
    emailsError = '';
    const threads = listFiles(cl, 'emails').then((rows) => {
      if (cl !== client || seq !== emailsLoadSeq) return;
      files = rows;
      emailThreads = rows;
    }).catch(() => {
      if (cl !== client || seq !== emailsLoadSeq) return;
      files = [];
      emailThreads = [];
      emailsError = 'Could not load emails.';
    }).finally(() => { if (cl === client && seq === emailsLoadSeq) emailsReady = true; });
    const contacts = getClientContacts(cl)
      .then((rows) => { if (cl === client && seq === emailsLoadSeq) clientEmails = rows; })
      .catch(() => { if (cl === client && seq === emailsLoadSeq) clientEmails = []; });
    const internal = getInternalContacts()
      .then((rows) => { if (cl === client && seq === emailsLoadSeq) internalEmails = rows; })
      .catch(() => { if (cl === client && seq === emailsLoadSeq) internalEmails = []; });
    const promise = Promise.all([threads, contacts, internal]).then(() => {});
    emailsLoad = { client: cl, promise };
    return promise;
  }

  let docsReady = $state(false);
  async function loadDocuments(c?: string) {
    const cl = c || client;
    try {
      const rows = await listFiles(cl, 'documents');
      if (cl === client) docFiles = rows;
    } catch { if (cl === client) docFiles = []; }
    finally { if (cl === client) docsReady = true; }
  }

  let notesReady = $state(false);
  async function loadNotes(c?: string) {
    const cl = c || client;
    try {
      const rows = await listFiles(cl, 'strategy');
      if (cl === client) noteFiles = rows;
    } catch { if (cl === client) noteFiles = []; }
    if (cl !== client) return;
    notesReady = true;
    noteSelected = '';
    noteContent = '';
    noteEditMode = false;
  }

  let noteLoading = $state(false);
  const noteLabels: Record<string, {label: string; desc: string}> = {
    'rolling_summary.md': { label: 'Client Summary', desc: 'Summary of relationship according to your partner, read when needed for client context.' },
    'recent_updates.md': { label: 'Recent Changes', desc: 'New activity since last summary refresh. Merged into Client Summary periodically.' },
    'web_research.md': { label: 'Web Research', desc: 'Company information, contacts, and news gathered from web searches during prospecting.' },
    'session_notes.md': { label: 'Memory (legacy)', desc: 'Old format session notes. New sessions are saved as individual files.' },
  };

  function noteLabel(name: string): { label: string; desc: string } | undefined {
    if (noteLabels[name]) return noteLabels[name];
    if (name.startsWith('memory_')) {
      // memory_2026-03-26.md or memory_2026-03-26_2.md
      const dateStr = name.slice(7, 17);
      const suffix = name.replace(/^memory_\d{4}-\d{2}-\d{2}/, '').replace(/\.md$/, '').replace(/^_/, '');
      const label = suffix ? `Memory ${dateStr} #${suffix}` : `Memory ${dateStr}`;
      return { label, desc: 'Session notes' };
    }
    if (name.includes('_session_')) {
      const dateStr = name.slice(0, 10);
      const slug = name.replace(/^\d{4}-\d{2}-\d{2}_session_/, '').replace(/\.md$/, '').replace(/_/g, ' ');
      const label = slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : 'Session';
      return { label, desc: dateStr };
    }
    return undefined;
  }

  async function selectNote(f: FileEntry) {
    noteSelected = f.name;
    noteEditMode = false;
    noteContent = '';
    noteLoading = true;
    try {
      const result = await readFileContent(client, 'strategy', f.name);
      const content = typeof result === 'string' ? result : (result as any).content ?? '';
      noteContent = content || '*(Empty file)*';
      noteEditContent = content;
    } catch { noteContent = '*(Failed to load)*'; }
    noteLoading = false;
  }

  async function saveNote() {
    if (!noteSelected) return;
    noteSaving = true;
    try {
      const { saveFileContent } = await import('../lib/api');
      await saveFileContent(client, 'strategy', noteSelected, noteEditContent);
      noteContent = noteEditContent;
      noteEditMode = false;
    } catch (err: any) {
      alert('Save failed: ' + (err.message || ''));
    }
    noteSaving = false;
  }

  async function loadCalls(c?: string) {
    const cl = c || client;
    try {
      const rows = await getClientCalls(cl);
      if (cl === client) callEntries = rows;
    } catch { if (cl === client) callEntries = []; }
    if (cl !== client) return;
    callsReady = true;
    expandedCall = '';
    callContent = '';
    callTranscript = '';
    showTranscript = false;
  }

  function callTypeLabel(type: string): string {
    if (type === 'meeting') return 'Client Call';
    if (type === 'strategy') return 'Internal Strategy';
    return 'Solo';
  }

  function callTypeFolder(type: string): string {
    // DB stores "meeting" but GCS folder is "meetings"
    return type === 'meeting' ? 'meetings' : 'strategy';
  }

  async function expandCall(entry: CallEntry) {
    if (expandedCall === entry.filename) {
      expandedCall = '';
      callContent = '';
      callTranscript = '';
      showTranscript = false;
      return;
    }
    expandedCall = entry.filename;
    callContent = '';
    callTranscript = '';
    showTranscript = false;
    loadingCall = true;
    try {
      const r = await readCall(entry.client, callTypeFolder(entry.type), entry.filename);
      callContent = r.content;
    } catch {
      callContent = '(Unable to load call summary)';
    }
    loadingCall = false;
  }

  async function toggleTranscript(entry: CallEntry) {
    if (showTranscript) {
      showTranscript = false;
      return;
    }
    if (!entry.has_transcript || !entry.transcript_filename) return;
    showTranscript = true;
    if (!callTranscript) {
      try {
        const r = await readTranscript(entry.client, callTypeFolder(entry.type), entry.transcript_filename);
        callTranscript = r.content;
      } catch {
        callTranscript = '(Unable to load transcript)';
      }
    }
  }

  let outreachReady = $state(false);
  async function loadOutreach(c?: string) {
    const cl = c || client;
    try {
      const rows = await getOutreachRecommendations(cl);
      if (cl === client) outreachDocs = rows;
    } catch { if (cl === client) outreachDocs = []; }
    finally { if (cl === client) outreachReady = true; }
  }

  async function loadPersonas() {
    try {
      personasData = await getPersonas();
      const stored = viewStorage.getItem('selectedPersona') || '';
      // Default to the user's own (human) persona
      const humanKey = Object.entries(personasData.personas).find(([_, p]) => p.label?.includes('(Human)'))![0];
      selectedPersona = (stored && personasData.personas[stored]) ? stored : humanKey;
      viewStorage.setItem('selectedPersona', selectedPersona);
    } catch { /* ignore */ }
  }

  let followupLoadSeq = 0;
  async function loadFollowup(c?: string) {
    const cl = c || client;
    const seq = ++followupLoadSeq;
    try {
      const r = await getFollowup(cl);
      if (cl !== client || seq !== followupLoadSeq) return;
      companyFollowup = r.follow_up || '';
      setClientStage(cl, r.stage || '');
      followupOverride = r.override ? { ...r.override, reason: r.override.reason || '' } : null;
      if (r.override) {
        followupDate = r.override.date;
        followupReason = r.override.reason || '';
      }
    } catch { /* ignore */ }
  }

  async function handleSetFollowup() {
    if (!followupDate) return;
    const cl = client;
    const date = followupDate;
    const reason = followupReason;
    await setFollowup(cl, date, reason);
    if (cl !== client) return;
    ++followupLoadSeq;
    companyFollowup = date;
    followupOverride = { date, reason, set_by: 'manual' };
  }

  async function handleClearFollowup() {
    const cl = client;
    const result = await clearFollowup(cl);
    if (cl !== client) return;
    ++followupLoadSeq;
    companyFollowup = result.follow_up;
    followupOverride = null;
    followupDate = '';
    followupReason = '';
  }

  // Point `getThreadMeta` at this thread. Deliberately does NOT read the body: the
  // content it used to fetch had no reader, which is the wasted GCS read that made this
  // path slower than prod. An empty tid is the better default anyway — the backend then
  // resolves the id from the stem it was actually given (routers/threads.py:259).
  function selectThread(f: FileEntry) {
    selectedThreadId = (f as any).thread_id || '';
  }

  async function openChatDraft(assistantMsg?: string) {
    draftedThisSession = true;  // suggested row now offers only "Save to memory"
    mainSeg = 'threads';  // George's "Draft email" opens the composer in the Emails panel
    const nonSystemMsgs = messages.filter(m => m.role !== 'system');
    if (nonSystemMsgs.length > 0) {
      const convoLines = nonSystemMsgs.map(m => `${m.role.toUpperCase()}: ${m.content}`).join('\n');
      chatDraftContext = `## Recent Chat (use as primary context)\n${convoLines.slice(0, 4000)}`;
    } else {
      chatDraftContext = '';
    }
    chatDraftTo = '';
    chatDraftToManual = '';
    chatDraftSubject = '';
    chatDraftBody = '';
    chatDraftNotes = '';
    chatDraftInstruction = '';
    chatDraftThread = '';
    chatDraftMeta = { default_to: '', default_subject: '', gmail_thread_id: '', cc_participants: [] };
    chatDraftCcClient = []; chatDraftCcInternal = []; chatDraftCcOther = '';
    resetAttachments();

    // Ask the AI to draft a structured email based on the conversation
    if (assistantMsg) {
      chatLoading = true;
      try {
        const personaKey = selectedPersona || viewStorage.getItem('selectedPersona') || '';
        const userName = personasData?.personas[personaKey]?.label || personasData?.personas[personaKey]?.name || 'Daniel';
        // Include thread list so AI can recommend which thread to reply to
        const threadList = emailThreads.map((t: any, i: number) => `${i + 1}. ${t.clean_name || t.stem}`).join('\n');
        const threadCtx = threadList
          ? `\n\nAvailable email threads:\n0. New email (start a fresh thread)\n${threadList}\n\nPick the best thread to reply to (or 0 for new). Put your choice as "Thread: <number>" on a separate line before the email.`
          : '';
        const draftPrompt = `Please draft the email now. I am ${userName} — write the email FROM me, not from anyone else.${threadCtx}\n\nFormat your response as:\nThread: <number>\nTo: email@domain.com\nSubject: ...\n\n<email body>\n\nKeep the subject line direct and professional — no marketing language. Close the body with a specific, concrete next step grounded in what was actually discussed; never a vague throwaway CTA like "worth a quick call?", "open to a chat?", or "would love to connect" — if there is no genuine next step, just end naturally. End with a short sign-off and my first name. No signature block. Use the established email pattern from existing contacts at this company.`;
        // Send draft prompt but don't show it in chat
        const draftMessages = [...messages.filter(m => m.role !== 'system'), { role: 'user' as const, content: draftPrompt }];
        const r = await chatApi({ client, mode: chatMode, messages: draftMessages });
        // Parse structured response
        const lines = r.reply.split('\n');
        let to = '', subject = '', threadChoice = '', bodyStart = -1;
        for (let i = 0; i < lines.length; i++) {
          const l = lines[i].replace(/\*\*/g, '').trim();
          if (l.match(/^Thread:\s*/i) && !threadChoice) threadChoice = l.replace(/^Thread:\s*/i, '').trim();
          else if (l.match(/^To:\s*/i) && !to) to = l.replace(/^To:\s*/i, '').trim();
          else if (l.match(/^Subject:\s*/i) && !subject) subject = l.replace(/^Subject:\s*/i, '').trim();
          else if ((to || subject) && l === '' && bodyStart < 0) bodyStart = i + 1;
        }
        if (bodyStart >= 0) {
          let rawBody = lines.slice(bodyStart).join('\n').trim();
          // Strip AI notes after --- separator — show them below the email
          const separatorIdx = rawBody.indexOf('\n---\n');
          if (separatorIdx > 0) {
            chatDraftNotes = rawBody.slice(separatorIdx + 5).trim();
            rawBody = rawBody.substring(0, separatorIdx).trim();
          }
          // Hand the body to EmailBodyEditor as plain markdown-flavored
          // text (asterisks for bold, real newlines). The editor's
          // plainToHtml will convert BOTH ** → <b> AND \n → <br>.
          // Previously we pre-converted \n to <br> here, which made
          // EmailBodyEditor's isHtml() heuristic return true → it
          // skipped plainToHtml() entirely → ** stayed literal.
          chatDraftBody = rawBody;
        }
        chatDraftTo = to;
        chatDraftSubject = subject || 'Draft';
        // Apply thread selection
        let threadMatched = false;
        if (threadChoice) {
          const num = parseInt(threadChoice, 10);
          if (num === 0 || threadChoice.toLowerCase() === 'new') {
            selectChatThread('__new', true);
            threadMatched = true;
          } else if (!isNaN(num) && num >= 1 && num <= emailThreads.length) {
            selectChatThread(emailThreads[num - 1].stem, true);
            threadMatched = true;
          }
        }
        // Fallback: if subject starts with RE:/Re:, match by subject similarity
        if (!threadMatched && subject && /^re:/i.test(subject)) {
          const cleanSubject = subject.replace(/^re:\s*/i, '').toLowerCase().trim();
          const match = emailThreads.find((t: any) => {
            const threadName = (t.clean_name || t.stem || '').toLowerCase().replace(/[-_]/g, ' ');
            return threadName.includes(cleanSubject.slice(0, 30)) || cleanSubject.includes(threadName.slice(0, 30));
          });
          if (match) {
            selectChatThread(match.stem, true);
            threadMatched = true;
          }
        }
        if (!threadMatched) {
          selectChatThread('__new', true);
        }
      } catch (err: any) {
        messages = [...messages, { role: 'assistant', content: 'Error drafting: ' + (err.message || '') }];
      }
      chatLoading = false;
    }

    chatDraftOpen = true;
  }

  async function selectChatThread(stem: string, preserveFields = false) {
    chatDraftThread = stem;
    if (stem && stem !== '__new') {
      chatDraftMeta = await getThreadMeta(client, stem, selectedThreadId);
      if (!preserveFields) {
        chatDraftTo = chatDraftMeta.default_to;
        chatDraftSubject = chatDraftMeta.default_subject;
        chatDraftCcClient = chatDraftMeta.cc_participants || [];
      }
    } else {
      chatDraftMeta = { default_to: '', default_subject: '', gmail_thread_id: '', cc_participants: [] };
      if (!preserveFields) {
        chatDraftTo = '';
        chatDraftSubject = '';
        chatDraftCcClient = [];
      }
    }
  }

  function buildCc(ccClient: string[], ccInternal: string[], ccOther: string): string {
    return [...ccClient, ...ccInternal, ...ccOther.split(',').map(s => s.trim()).filter(Boolean)].join(', ');
  }

  function toggleOutreachDoc(id: string) {
    if (selectedOutreachIds.includes(id)) {
      selectedOutreachIds = selectedOutreachIds.filter(x => x !== id);
    } else {
      selectedOutreachIds = [...selectedOutreachIds, id];
    }
  }

  function removeCustomFile(idx: number) {
    customFiles = customFiles.filter((_, i) => i !== idx);
  }

  function handleCustomFiles(e: Event) {
    const input = e.target as HTMLInputElement;
    if (input.files) {
      customFiles = [...customFiles, ...Array.from(input.files)];
    }
    input.value = '';
  }


  async function handleGenerate(
    threadId: string, contextMd: string,
    setTo: (v: string) => void, setSubject: (v: string) => void, setBody: (v: string) => void,
    currentTo: string, currentSubject: string, instruction: string,
    setCc?: (v: string[]) => void,
    setInternalCc?: (v: string[]) => void,
    onResult?: (r: any) => void,
  ) {
    generating = true;
    try {
      const r = await generateEmail({
        client,
        instruction,
        current_to: currentTo,
        current_subject: currentSubject,
        thread_id: threadId,
        context_md: contextMd,
        persona: selectedPersona || undefined,
      });
      // Handle To — if multiple, first goes to To, rest go to CC
      let toAddrs = r.to ? r.to.split(',').map((e: string) => e.trim()).filter(Boolean) : [];
      let ccAddrs = r.cc ? r.cc.split(',').map((e: string) => e.trim()).filter(Boolean) : [];
      if (toAddrs.length > 1) {
        ccAddrs = [...toAddrs.slice(1), ...ccAddrs];
        toAddrs = [toAddrs[0]];
      }
      if (toAddrs.length) setTo(toAddrs[0]);
      if (r.subject) setSubject(r.subject);
      setBody(r.body);
      // Split CC into internal (geomet.ai) and external
      if (ccAddrs.length && setCc) {
        const internal = ccAddrs.filter(e => e.includes('geomet.ai'));
        const external = ccAddrs.filter(e => !e.includes('geomet.ai'));
        setCc(external);
        if (setInternalCc) setInternalCc(internal);
      }
      if (onResult) onResult(r);
    } catch (err: any) {
      showError('Generate failed: ' + (err.message || 'Unknown error'));
    }
    generating = false;
  }

  // (Re)generate the draft body from the current context/instruction. Shared by the
  // instruction-row "Generate" button and the "✦ Regenerate" tool in the action row.
  function runGenerate() {
    let ctx = chatDraftContext;
    if (chatDraftThread === '') {
      // AI decides — give it a numbered list
      const threadList = emailThreads.map((t, i) => `${i + 1}. ${t.clean_name || t.stem}`).join('\n');
      ctx += `\n\n## Available email threads:\n0. New email (start a fresh thread)\n${threadList}\n\nPick the best thread to reply to (or 0 for new). Put your choice as **Thread:** <number> in your response.`;
    } else if (chatDraftThread === '__new') {
      ctx += '\n\n## This is a NEW email (not a reply to any existing thread).';
    } else {
      ctx += `\n\n## This is a REPLY to the thread: ${chatDraftThread}`;
    }
    const wasAiDecides = chatDraftThread === '';
    handleGenerate(
      chatDraftMeta.gmail_thread_id || '',
      ctx,
      v => chatDraftTo = v,
      v => { chatDraftSubject = v; },
      v => { chatDraftBody = v; },
      chatDraftTo, chatDraftSubject,
      // Prepend the type-specific brief carried in from Discuss
      // (LIGHT/HEAVY/COLD/MEETING_FOLLOWUP/PROSPECTING/INBOUND) so the
      // regen lands in the same flavor the original draft used. The
      // user's own typed-in instruction stacks on top.
      chatDraftBrief
        ? (chatDraftInstruction
            ? chatDraftBrief + '\n\nAdditional from user: ' + chatDraftInstruction
            : chatDraftBrief)
        : chatDraftInstruction,
      v => chatDraftCcClient = v,
      v => chatDraftCcInternal = v,
      wasAiDecides ? (r: any) => {
        const choice = r.thread_choice;
        if (choice) {
          const num = parseInt(choice, 10);
          if (num === 0 || choice.toLowerCase() === 'new') {
            selectChatThread('__new', true);
          } else if (!isNaN(num) && num >= 1 && num <= emailThreads.length) {
            selectChatThread(emailThreads[num - 1].stem, true);
          } else {
            const match = emailThreads.find(t => t.stem === choice);
            if (match) selectChatThread(match.stem, true);
          }
        }
      } : undefined,
    );
  }

  async function handleSave(threadId: string, to: string, subject: string, body: string, cc: string) {
    if (!to) { showToast('Set a To address'); return; }
    saving = true;
    try {
      await saveDraft({
        to, subject, body, cc, thread_id: threadId, client,
        outreach_doc_ids: selectedOutreachIds.length ? selectedOutreachIds : undefined,
        files: customFiles.length ? customFiles : undefined,
      });
      if (selectedOutreachIds.length) {
        await markOutreachSent(client, selectedOutreachIds);
        loadOutreach(client);
      }
      showToast(threadId ? 'Reply draft saved' : 'Draft saved');
      resetAttachments();
    } catch (err: any) {
      showError('Save failed: ' + (err.message || 'Unknown error'));
    }
    saving = false;
  }

  let sending = $state(false);
  let scheduleOpen = $state(false);
  let scheduleDate = $state('');
  let scheduleTime = $state('');

  // Click on a <input type="date">/<input type="time"> should pop the native
  // calendar/clock. In Electron+Chromium, plain focus doesn't always trigger
  // it — explicitly calling showPicker() does. Wrapped in a try/catch since
  // showPicker() throws if the input was already showing one (e.g. rapid
  // double-clicks).
  function openPicker(e: Event) {
    const el = e.currentTarget as HTMLInputElement;
    try { el.showPicker?.(); } catch {}
  }

  // Pretty label for the back-crumb. Internal view names (snake_case
  // bits like 'super-admin' or 'george'->'voice') get a hand-curated
  // mapping; anything unknown falls back to "title-casing" the slug.
  // Must cover every view in App.svelte's `pages` set — that gate decides what
  // `lastTopLevelView` can hold. A view missing here renders its raw id.
  const _BACK_LABELS: Record<string, string> = {
    today: 'Today', dashboard: 'Dashboard', contacts: 'Contacts',
    prospecting: 'Prospecting', sequences: 'Sequences', teach: 'Teach George',
    george: 'Live Calls', settings: 'Settings',
  };
  function backLabel(v: string): string {
    return _BACK_LABELS[v] || v.replace(/_/g, ' ');
  }

  async function handleSend(threadId: string, to: string, subject: string, body: string, cc: string) {
    if (!to) { showToast('Set a To address'); return; }
    if (!confirm('Send this email now?')) return;
    sending = true;
    try {
      const { sendEmail } = await import('../lib/api');
      const fd = new FormData();
      fd.append('to', to); fd.append('subject', subject); fd.append('body', body);
      fd.append('cc', cc || ''); fd.append('thread_id', threadId || ''); fd.append('client', client);
      if (selectedOutreachIds.length) fd.append('outreach_doc_ids', JSON.stringify(selectedOutreachIds));
      for (const f of customFiles) fd.append('files', f);
      await sendEmail(fd);
      if (selectedOutreachIds.length) {
        await markOutreachSent(client, selectedOutreachIds);
        loadOutreach(client);
      }
      const sentTo = to;
      const sentSubject = subject;
      showToast('Email sent');
      lastSentNotice = `Sent "${sentSubject}" to ${sentTo} at ${new Date().toLocaleTimeString()}`;
      resetAttachments();
      // Clear form
      chatDraftTo = ''; chatDraftToManual = ''; chatDraftSubject = ''; chatDraftBody = '';
      chatDraftNotes = ''; chatDraftCcClient = []; chatDraftCcInternal = []; chatDraftCcOther = '';
    } catch (err: any) {
      showError('Send failed: ' + (err.message || 'Unknown error'));
    }
    sending = false;
  }

  async function handleScheduleSend(threadId: string, to: string, subject: string, body: string, cc: string) {
    if (!to) { showToast('Set a To address'); return; }
    if (!scheduleDate || !scheduleTime) { showToast('Pick a date and time'); return; }
    sending = true;
    try {
      const { scheduleEmail } = await import('../lib/api');
      const sendAt = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
      const fd = new FormData();
      fd.append('to', to); fd.append('subject', subject); fd.append('body', body);
      fd.append('cc', cc || ''); fd.append('thread_id', threadId || ''); fd.append('client', client);
      fd.append('send_at', sendAt);
      if (selectedOutreachIds.length) fd.append('outreach_doc_ids', JSON.stringify(selectedOutreachIds));
      for (const f of customFiles) fd.append('files', f);
      await scheduleEmail(fd);
      showToast(`Email scheduled for ${scheduleDate} ${scheduleTime}`);
      lastSentNotice = `Scheduled "${subject}" to ${to} for ${scheduleDate} ${scheduleTime}`;
      scheduleOpen = false;
      resetAttachments();
      // Drop focus before clearing — the RichTextarea inside EmailBodyEditor
      // refuses to repaint its contenteditable while the user has the cursor
      // in it (to avoid clobbering live typing), so a value='' assignment
      // would update the state but leave the visible body intact. handleSend
      // gets a confirm() dialog that takes focus for free; schedule has no
      // dialog, so we drop focus explicitly.
      (document.activeElement as HTMLElement)?.blur?.();
      // Clear form
      chatDraftTo = ''; chatDraftToManual = ''; chatDraftSubject = ''; chatDraftBody = '';
      chatDraftNotes = ''; chatDraftCcClient = []; chatDraftCcInternal = []; chatDraftCcOther = '';
    } catch (err: any) {
      showError('Schedule failed: ' + (err.message || 'Unknown error'));
    }
    sending = false;
  }

  let chatQueue = $state<string[]>([]);

  async function handleChat() {
    if (!chatInput.trim()) return;
    const userMsg = chatInput.trim();
    chatInput = '';

    if (chatLoading) {
      // Queue the message — it'll be processed after the current response
      chatQueue = [...chatQueue, userMsg];
      messages = [...messages, { role: 'user', content: userMsg }];
      return;
    }

    messages = [...messages, { role: 'user', content: userMsg }];
    await processChat();
  }

  async function processChat() {
    chatLoading = true;
    try {
      const r = await chatApi({ client, messages, ref_thread: refThread, mode: chatMode });
      if (r.tool_calls?.length) {
        const toolSummary = r.tool_calls.map((t: any) => {
          if (t.tool === 'web_search') return `🔍 Searched: "${t.input}"`;
          if (t.tool === 'read_client') return `📋 Looked up: ${t.input}`;
          if (t.tool === 'read_threads') return `📧 Listed threads: ${t.input}`;
          if (t.tool === 'read_email') return `✉️ Read email: ${t.input}`;
          if (t.tool === 'read_knowledge') return `📚 Read: ${t.input}`;
          if (t.tool === 'read_calls') return `📞 Call history: ${t.input}`;
          if (t.tool === 'read_memory') return `🧠 Read memory: ${t.input}`;
          return `🔧 ${t.tool}: ${t.input}`;
        }).join('\n');
        messages = [...messages, { role: 'system', content: toolSummary }];
      }
      messages = [...messages, { role: 'assistant', content: r.reply }];
    } catch (err: any) {
      messages = [...messages, { role: 'assistant', content: 'Error: ' + (err.message || 'Request failed') }];
    }
    chatLoading = false;

    // Process queued messages
    if (chatQueue.length > 0) {
      chatQueue = [];
      await processChat();
    }
  }

  let savingInsights = $state(false);
  let lastSavedSession = $state('');
  async function handleSaveInsights() {
    if (savingInsights) return;
    savingInsights = true;
    try {
      const r = await saveInsights({ client, messages, update_file: lastSavedSession || undefined });
      if (r.saved_to) lastSavedSession = r.saved_to;
      showToast(`Saved to ${r.saved_to}`);
      await loadNotes();
    } catch (err: any) {
      showError('Save failed: ' + (err.message || ''));
    }
    savingInsights = false;
  }


  let noteFiles = $state<FileEntry[]>([]);
  let noteSelected = $state('');
  let noteContent = $state('');
  let noteEditMode = $state(false);
  let memoryExpanded = $state(false);
  // ONE list, not "notes" + "memories". They were split into a 📋 group and a 🧠
  // "Memory · saved insights" group inside a tab already called Memory — two names,
  // two icons, one concept. A rolling summary and a saved session are both things the
  // system remembers about this client; the only real difference is who wrote them,
  // which is not a reason to make the user look in two places.
  // Order is still meaningful: the always-present system files first (they're the
  // client's standing context), then saved sessions newest-first behind them.
  const isSavedSession = (n: string) => n.startsWith('memory_') || n.includes('_session_');
  let orderedNotes = $derived([
    ...noteFiles.filter(f => !isSavedSession(f.name)),
    ...noteFiles.filter(f => isSavedSession(f.name)),
  ]);
  let noteEditContent = $state('');
  let noteSaving = $state(false);

  let docFolder = $state('documents');
  let docEditMode = $state(false);
  let docEditContent = $state('');
  let docLoadedContent = $state('');
  let docSaving = $state(false);

  async function viewDoc(f: any) {
    if (docPreviewUrl) { URL.revokeObjectURL(docPreviewUrl); docPreviewUrl = ''; }
    docPreviewError = false;
    docSelected = f.name;
    docFolder = (f as any)._folder || 'documents';
    docEditMode = false;
    docLoadedContent = '';
    docEditContent = '';
    if (isSupportView() && isPdf(f.name)) {
      try {
        const response = await fetch(getDocumentUrl(client, f.name), { credentials: 'include' });
        if (!response.ok) throw new Error(`Preview unavailable (${response.status})`);
        const objectUrl = URL.createObjectURL(await response.blob());
        if (docSelected === f.name) docPreviewUrl = objectUrl;
        else URL.revokeObjectURL(objectUrl);
      } catch { docPreviewUrl = ''; docPreviewError = true; }
    }
    if (f.name.endsWith('.md') && docFolder === 'strategy') {
      try {
        const result = await readFileContent(client, 'strategy', f.name);
        docLoadedContent = (result as any).content || result as any;
        docEditContent = docLoadedContent;
      } catch { docLoadedContent = ''; }
    }
  }

  async function saveDocEdit() {
    if (!docSelected || !docFolder) return;
    docSaving = true;
    try {
      const { saveFileContent } = await import('../lib/api');
      await saveFileContent(client, docFolder, docSelected, docEditContent);
      docLoadedContent = docEditContent;
      docEditMode = false;
    } catch (err: any) {
      alert('Save failed: ' + (err.message || ''));
    }
    docSaving = false;
  }

  function isPdf(name: string): boolean {
    return name.toLowerCase().endsWith('.pdf');
  }

  /* One outline family keyed by extension, in the same stroke language as `.clip-icon` and
     the back-crumb chevron. Returns the PATH data only; the `<svg>` wrapper lives in the
     markup so the two call sites can size it (16px in the row, 40px in the preview) without
     two copies of the icon set. Emoji were the previous answer and rendered as a different
     picture in every font. */
  function fileIconPath(name: string): string {
    const ext = name.split('.').pop()?.toLowerCase() || '';
    // image: framed picture with a horizon
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext))
      return 'M3 3h18v18H3zM3 15l5-5 4 4 3-3 6 6';
    // spreadsheet: a grid
    if (['xls', 'xlsx', 'csv'].includes(ext))
      return 'M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18';
    // any document: a page with a folded corner, plus rules for text-bearing types
    const page = 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6';
    if (['pdf', 'doc', 'docx', 'md', 'txt', 'rtf'].includes(ext))
      return page + 'M8 13h8M8 17h5';
    return page;
  }

  // Non-success messages render in the red toast. Detected HERE rather than at
  // the ~30 call sites in this file: each already states its outcome in words,
  // and a missed flag fails silently back to green — which is the bug this
  // fixes. Pass `true` explicitly to override.
  const TOAST_FAILURE = /\b(fail|failed|error|invalid|required|can'?t|cannot|empty|denied|unable)\b|^(pick|set|enter|select) /i;
  function showToast(msg: string, isError = TOAST_FAILURE.test(msg)) {
    toast = msg;
    toastError = isError;
    setTimeout(() => toast = '', isError ? 5000 : 3000);
  }

  // Anything reaching a `catch` IS a failure — say so explicitly rather than hoping the
  // wording matches TOAST_FAILURE. The trap is `err?.message || 'X failed'`: the FALLBACK
  // carries the safe word, but when the server does send a message ("Draft not found",
  // "5 emails are already pinned") that fallback never runs, and the toast came out green.
  const showError = (msg: string) => showToast(msg, true);

</script>

{#snippet attachmentWidget()}
  <!-- Attachments laid out like Today's draft editor: labeled sections + pill
       chips + a `.today-mini-btn` "+ Attach file" button (shared vocabulary),
       always visible rather than a collapsed toggle. -->
  {#if outreachDocs.length > 0}
    <div class="text-xs text-dim">Attachments</div>
    <div class="chip-row mb-2" style="margin-top:4px;">
      {#each outreachDocs as doc}
        <span
          class="chip attach" class:selected={selectedOutreachIds.includes(doc.id)}
          role="button" tabindex="0"
          onclick={() => toggleOutreachDoc(doc.id)}
          onkeydown={(ev) => (ev.key === 'Enter' || ev.key === ' ') && toggleOutreachDoc(doc.id)}
          title={doc.when_to_attach}
        ><svg class="clip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>{doc.title}</span>
      {/each}
    </div>
  {/if}
  <div class="text-xs text-dim">Custom files</div>
  <div class="mb-3" style="margin-top:4px; display:flex; flex-wrap:wrap; gap:6px; align-items:center;">
    {#each customFiles as f, i}
      <span class="chip selected" style="display:inline-flex; align-items:center; gap:4px;">
        {f.name}
        <span
          role="button" tabindex="0" style="cursor:pointer; font-size:14px; line-height:1;"
          onclick={() => removeCustomFile(i)}
          onkeydown={(ev) => (ev.key === 'Enter' || ev.key === ' ') && removeCustomFile(i)}
        >&times;</span>
      </span>
    {/each}
    <label class="today-mini-btn" style="display:inline-flex; align-items:center; gap:4px; cursor:pointer;">
      + Attach file
      <input type="file" multiple class="sr-only" onchange={handleCustomFiles} />
    </label>
  </div>
{/snippet}

<div class="client-header-wrap">
  <button class="back-crumb"
          title="Back"
          onclick={() => {
            // Prefer browser history so the URL bar matches what we restore.
            // Fall back to the saved prior top-level view (or Today) if
            // there's no prior entry (deep link in a fresh tab).
            if (window.history.length > 1) window.history.back();
            else $currentView = $lastTopLevelView || 'today';
          }}>
    {'← '}Back{$lastTopLevelView ? ` to ${backLabel($lastTopLevelView)}` : ''}
  </button>
  <div class="client-header">
    <div class="page-title">{client.replace(/_/g, ' ')}</div>
    {#if clientStage}
      <span class="client-stage-pill" style="color:{stageColor(clientStage)};border-color:color-mix(in srgb, {stageColor(clientStage)} 40%, transparent);">{$stageLabel(clientStage)}</span>
    {/if}
    {#if clientFollowup}
      <span class="et-badge status-{clientFollowup.tone}"
            title={clientFollowup.label === 'Parked' ? 'No active follow-up' : `Follow-up due ${fmtShortDate(companyFollowup || '')}`}>{clientFollowup.label}</span>
    {/if}
  </div>
</div>

<div class="split-shell cv-shell" bind:this={shellEl} class:dragging={rail.dragging} class:collapsed={rail.collapsed} style="--rail-w: {rail.w}px">
  <!-- LEFT: George, persistent workspace assistant -->
  <aside class="split-rail">
    {#if !rail.showTab}{@render georgeRail()}{/if}
  </aside>

  <!-- Splitter: ONE visible control, plus the bar itself.
       The handle is the hide-George button — nothing else. Resizing is the bar, and the
       coral highlight it takes on hover (plus the col-resize cursor) is the whole signal;
       it doesn't need a second widget explaining it. Earlier passes got this wrong twice:
       first by putting resize AND collapse on one 9px button with collapse on a
       DOUBLE-click documented only in a `title` tooltip, then by splitting them into two
       stacked buttons, which just moved the ambiguity rather than removing it. -->
  <div class="split-divider" class:collapsed={rail.collapsed}>
    {#if !rail.collapsed}
      <!-- The drag target fills the column (and 4px either side). A real <button> so the
           arrow-key resize in rail.onKey() is reachable and announced; the wrapper stays a
           plain div with no handlers, which also keeps the handle <button> from being
           nested inside another button. Sibling of .split-grip, not parent — a press on the
           handle targets the handle, a press anywhere else on the bar starts a drag, with
           no propagation juggling. Absent when collapsed: nothing to resize. -->
      <button type="button" class="split-drag"
              aria-label="Resize {$agentNameStore || 'George'} panel — arrow keys adjust"
              onpointerdown={rail.startDrag} onkeydown={rail.onKey}></button>
    {/if}
    <!-- ONE handle, both directions: ‹ pushes the panel away, › brings it back.
         It says nothing about George on purpose. Collapsing moves George into the tab bar
         (rail.showTab) — so a labelled "✦ George" tab here, which is what this was, put
         two identically-named controls side by side doing DIFFERENT things: the tab bar
         one switches the records pane, this one restores the rail. This is the layout
         toggle; the tab is the way to George. See DECISIONS.md. -->
    <button type="button" class="split-grip" onclick={rail.toggle}
            aria-expanded={!rail.collapsed}
            title="{rail.collapsed ? 'Show' : 'Hide'} {$agentNameStore || 'George'}"
            aria-label="{rail.collapsed ? 'Show' : 'Hide'} {$agentNameStore || 'George'} panel"
      >{rail.collapsed ? '›' : '‹'}</button>
  </div>

  <!-- RIGHT: the records -->
  <section class="split-main cv-main">
    <div class="tabs">
      {#if rail.showTab}
        <div class="tab tab-assist" class:active={mainSeg === 'george'}
          role="button" tabindex="0"
          onclick={() => mainSeg = 'george'}
          onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && (mainSeg = 'george')}>
          <span class="spark">✦</span> {$agentNameStore || 'George'}
        </div>
      {/if}
      <div class="tab" class:active={mainSeg === 'threads'}
        role="button" tabindex="0"
        onclick={() => mainSeg = 'threads'}
        onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && (mainSeg = 'threads')}>
        Emails
      </div>
      <div class="tab" class:active={mainSeg === 'people'}
        role="button" tabindex="0"
        onclick={() => mainSeg = 'people'}
        onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && (mainSeg = 'people')}>
        Contacts
      </div>
      <div class="tab" class:active={mainSeg === 'notes'}
        role="button" tabindex="0"
        onclick={() => mainSeg = 'notes'}
        onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && (mainSeg = 'notes')}>
        Memory
      </div>
      <div class="tab" class:active={mainSeg === 'documents'}
        role="button" tabindex="0"
        onclick={() => mainSeg = 'documents'}
        onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && (mainSeg = 'documents')}>
        Files
      </div>
      <div class="tab" class:active={mainSeg === 'insights'}
        role="button" tabindex="0"
        onclick={() => mainSeg = 'insights'}
        onkeydown={(e) => (e.key === 'Enter' || e.key === ' ') && (mainSeg = 'insights')}>
        Pulse
      </div>
    </div>

    <div class="cv-mainbody">
      {#if mainSeg === 'george'}<div class="seg-assist">{@render georgeRail()}</div>
      {:else if mainSeg === 'threads'}{@render segThreads()}
      {:else if mainSeg === 'people'}{@render segPeople()}
      {:else if mainSeg === 'documents'}{@render segDocs()}
      {:else if mainSeg === 'notes'}{@render segNotes()}
      {:else if mainSeg === 'insights'}{@render segInsights()}{/if}
    </div>
  </section>
</div>


<!-- ═══════════════ RECORDS SEGMENTS (snippets, rendered by cv-main above) ═══════════════ -->
{#snippet segThreads()}

  {#if folder === 'emails' && !topComposerOpen}
    <div class="drafts-head">
      <button type="button" class="btn-newemail" onclick={startNewEmail}>+ New email</button>
    </div>
  {/if}
  {#if topComposerOpen}
    {@render composeArea()}
  {/if}

  <!-- Email threads — the only reachable folder view (folder is always 'emails') -->
    {#if !emailsReady}
      <div class="segment-loading"><span class="spinner"></span> Loading emails…</div>
    {:else if emailsError}
      <div class="segment-loading">{emailsError} <button type="button" class="et-retry" onclick={() => loadEmails(client, true)}>Retry</button></div>
    {:else if files.length === 0}
      <div class="empty-state">
        <div class="empty-state-icon">{folder === 'emails' ? '📧' : folder === 'meetings' ? '📅' : '📋'}</div>
        <div class="empty-state-text">No {folder} found for this client.</div>
      </div>
    {:else}
      {#if folder === 'emails'}
        <div class="esearch">
          <svg class="esearch-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>
          <input placeholder="Search email threads…" bind:value={emailQuery} />
          {#if emailQuery}<button type="button" class="eq-x" onclick={() => emailQuery = ''} aria-label="Clear search">×</button>{/if}
        </div>
        {#if filteredEmailFiles.length === 0}
          <div class="eno">No threads match “{emailQuery}”.</div>
        {/if}
      {/if}
      <!-- Email threads as accordions — expand to read; Reply opens the draft inside the thread. -->
      {#each filteredEmailFiles as f}
        <div class="ethread" data-thread={f.name} class:open={expandedThreads.includes(f.name)}>
          <div class="et-head">
            <button type="button" class="et-toggle" onclick={() => toggleThread(f)}>
              <span class="et-caret" class:open={expandedThreads.includes(f.name)}>▸</span>
              <span class="et-main">
                <!-- .et-subj is nowrap+ellipsis; real subjects run longer than the
                     50-char slug did, so carry the full one in the tooltip. -->
                <span class="et-subj" title={prettyThreadName(f)}>{prettyThreadName(f)}</span>
                {#if f.summary}<span class="et-meta">{f.summary}</span>{/if}
              </span>
              <span class="et-date">{f.last_date ? daysAgo(f.last_date) : threadDate(f)}</span>
            </button>
          </div>
          {#if expandedThreads.includes(f.name)}
            <div class="et-body">
              {#if threadLoading[f.name]}
                <div style="text-align:center; padding:24px;"><span class="spinner spinner-lg"></span></div>
              {:else if threadContent[f.name]}
                {@const msgs = parseThreadMessages(threadContent[f.name])}
                {#each msgs as msg, i}
                  <div class="emsg" class:out={msg.direction.toLowerCase().includes('outbound')} class:mopen={isMsgOpen(f.name, i, msgs.length)}>
                    <button type="button" class="em-h" onclick={() => toggleMsg(f.name, i, msgs.length)}>
                      <span class="em-caret" class:open={isMsgOpen(f.name, i, msgs.length)}>▸</span>
                      <b>{msg.sender || '—'}</b>
                      <span>{msg.date ? fmtShortDate(msg.date.split(/[ T]/)[0]) : ''}{msg.direction ? ' · ' + msg.direction.toLowerCase() : ''}</span>
                    </button>
                    {#if isMsgOpen(f.name, i, msgs.length)}
                      <!-- unwrapHardBreaks: the sender's ~76-col plaintext wrap is theirs, not
                           our column width — see lib/markdown.ts. Only the INGESTED body gets
                           it; chat/draft markdown below keeps its authored newlines. -->
                      <div class="em-b md-content">{@html renderSafeMarkdown(unwrapHardBreaks(repairLostApostrophes(stripCids(msg.body))))}</div>
                    {:else}
                      <div class="em-snip">{msgSnippet(msg.body)}</div>
                    {/if}
                  </div>
                {/each}
                {#if folder === 'emails' && !(chatDraftOpen && draftThreadFile && draftThreadFile.name === f.name)}
                  <div class="ecard-respond"><button type="button" class="btn-newemail" onclick={() => replyToThread(f)}>Respond</button></div>
                {/if}
              {:else if threadError[f.name]}
                <!-- A failed load is NOT an empty thread — say so, and offer the retry
                     that the un-cached miss now makes possible. -->
                <div class="cd-none" style="padding:6px 0;">
                  Couldn’t load this thread.
                  <button type="button" class="et-retry" onclick={() => loadThreadContent(f)}>Retry</button>
                </div>
              {:else if f.summary}
                <div class="et-summary">{f.summary}</div>
              {:else}
                <div class="cd-none" style="padding:6px 0;">No message content.</div>
              {/if}

          <!-- Unified reply/draft composer — the SAME composeArea as "+ New email", rendered inside the thread it targets -->
          {#if chatDraftOpen && draftThreadFile && draftThreadFile.name === f.name}
            {@render composeArea()}
          {/if}
            </div>
          {/if}
        </div>
      {/each}
    {/if}

  <!-- Maintenance footer: rare, destructive Rebuild affordance lives here so
       it stays out of the day-to-day email workflow at the top of the tab. -->
  <div class="rebuild-footer">
    {#if !rebuildOpen}
      <button
        class="rebuild-footer-trigger"
        disabled={rebuilding}
        onclick={() => { rebuildOpen = true; rebuildConfirming = false; }}
      >
        {rebuilding ? '↻ Rebuilding…' : '↻ Rebuild this client…'}
      </button>
    {:else if !rebuildConfirming}
      <div class="rebuild-panel">
        <div class="rebuild-panel-title">Reset &amp; rebuild for {client.replace(/_/g, ' ')}</div>
        <label class="rebuild-check"><input type="checkbox" bind:checked={rebuildEmails} /> Emails</label>
        <label class="rebuild-check"><input type="checkbox" bind:checked={rebuildStrategy} /> Strategy &amp; call notes</label>
        <label class="rebuild-check"><input type="checkbox" bind:checked={rebuildContacts} /> Contacts</label>
        <div class="rebuild-panel-actions">
          <button class="small" onclick={() => rebuildOpen = false}>Cancel</button>
          <button
            class="small primary"
            disabled={!(rebuildEmails || rebuildStrategy || rebuildContacts)}
            onclick={() => rebuildConfirming = true}
          >Continue →</button>
        </div>
      </div>
    {:else}
      <div class="rebuild-panel rebuild-panel-confirm">
        <div class="rebuild-panel-title">Confirm rebuild</div>
        <p class="rebuild-confirm-copy">
          Rebuilding clears the selected data for this client and reprocesses it from
          raw emails. This takes a few minutes and cannot be undone.
        </p>
        <div class="rebuild-panel-actions">
          <button class="small" onclick={() => { rebuildConfirming = false; }}>Cancel</button>
          <button class="small danger" onclick={runRebuild}>Rebuild</button>
        </div>
      </div>
    {/if}
  </div>

{/snippet}

{#snippet segDocs()}
  {#if !docsReady}
    <div class="segment-loading"><span class="spinner"></span> Loading files…</div>
  {:else if docFiles.length === 0}
    <div class="empty-state">
      <div class="empty-state-icon">📁</div>
      <div class="empty-state-text">No files for this client.</div>
    </div>
  {:else}
    {#each docFiles as f}
      <div class="ethread">
        <div class="et-head">
          <button type="button" class="et-toggle" onclick={() => docSelected === f.name ? (docSelected = '') : viewDoc(f)}>
            <span class="et-caret" class:open={docSelected === f.name}>▸</span>
            <span class="doc-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d={fileIconPath(f.name)}/></svg></span>
            <span class="et-main">
              <span class="et-subj">{prettyDocName(f)}</span>
              {#if f.size_kb !== undefined}<span class="et-meta">{formatFileSize(f.size_kb * 1024)}</span>{/if}
            </span>
          </button>
          <a class="ghost-sm" href={getDocumentUrl(client, f.name)} download={f.name}
            onclick={(e) => void downloadSupportFile(e, getDocumentUrl(client, f.name), f.name).catch((err) => alert(err.message))}>Download</a>
        </div>
        {#if docSelected === f.name}
          <div class="et-body">
            {#if isPdf(f.name)}
              {#if !isSupportView() || docPreviewUrl}
              <iframe
                src={(isSupportView() ? docPreviewUrl : getDocumentUrl(client, f.name)) + '#toolbar=1&navpanes=0&view=FitH'}
                style="width:100%; height:60vh; border:1px solid var(--border); border-radius:var(--radius); background:var(--bg-surface);"
                title="PDF preview"
              ></iframe>
              {:else}<div class="text-dim">{docPreviewError ? 'Preview unavailable — use Download.' : 'Loading preview…'}</div>{/if}
            {:else}
              <div style="text-align:center; padding:24px; color:var(--text-muted);">
                <div class="doc-icon doc-icon-lg"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d={fileIconPath(f.name)}/></svg></div>
                Preview not available — <a href={getDocumentUrl(client, f.name)} download={f.name} style="color:var(--accent);"
                  onclick={(e) => void downloadSupportFile(e, getDocumentUrl(client, f.name), f.name).catch((err) => alert(err.message))}>download</a> to view.
              </div>
            {/if}
          </div>
        {/if}
      </div>
    {/each}
  {/if}
{/snippet}

{#snippet segNotes()}
  {#if !notesReady}
    <div class="segment-loading"><span class="spinner"></span> Loading memory…</div>
  {:else if noteFiles.length === 0}
    <div class="empty-state">
      <div class="empty-state-icon">🧠</div>
      <div class="empty-state-text">Nothing saved to memory yet — use “Save to memory” in the George panel.</div>
    </div>
  {:else}
    <!-- One list, one 🧠, no sub-header. Was two groups (📋 "notes" + a
         "🧠 Memory · saved insights" divider) inside a tab called Memory. -->
    {#each orderedNotes as f}
      {@const meta = noteLabel(f.name)}
      <div class="ethread">
        <div class="et-head">
          <button type="button" class="et-toggle" onclick={() => noteSelected === f.name ? (noteSelected = '') : selectNote(f)}>
            <span class="et-caret" class:open={noteSelected === f.name}>▸</span>
            <span class="doc-icon">🧠</span>
            <span class="et-main">
              <span class="et-subj">{meta?.label || prettyDocName(f)}</span>
              {#if meta}<span class="et-meta">{meta.desc}</span>{/if}
            </span>
          </button>
        </div>
        {#if noteSelected === f.name}{@render noteBody()}{/if}
      </div>
    {/each}
  {/if}
{/snippet}

{#snippet noteBody()}
  <div class="et-body">
    {#if noteLoading}
      <div style="text-align:center; padding:20px;"><span class="spinner spinner-lg"></span></div>
    {:else if noteEditMode}
      <textarea bind:value={noteEditContent} style="width:100%; min-height:40vh; font-size:13px; font-family:var(--font-mono); padding:12px; border:1px solid var(--border); border-radius:var(--radius); resize:vertical;"></textarea>
      <div style="display:flex; gap:6px; margin-top:8px;">
        <button class="ghost-sm" style="background:var(--accent); color:#fff; border-color:var(--accent);" disabled={noteSaving} onclick={saveNote}>{noteSaving ? 'Saving…' : 'Save'}</button>
        <button class="ghost-sm" onclick={() => { noteEditMode = false; noteEditContent = noteContent; }}>Cancel</button>
      </div>
    {:else}
      <div class="content-viewer md-content">{@html renderSafeMarkdown(noteContent)}</div>
      <div style="display:flex; gap:6px; margin-top:10px;">
        <button class="ghost-sm" onclick={() => { noteEditMode = true; noteEditContent = noteContent; }}>Edit</button>
        {#if noteSelected.startsWith('memory_') || noteSelected.includes('_session_')}
          <button class="ghost-sm" style="color:var(--red); border-color:color-mix(in srgb, var(--red) 40%, transparent);" onclick={async () => {
            if (!confirm('Delete this session note?')) return;
            try {
              const { deleteFile } = await import('../lib/api');
              await deleteFile(client, 'strategy', noteSelected);
              showToast('Session note deleted');
              noteSelected = ''; noteContent = '';
              await loadNotes();
            } catch (err) { showError('Delete failed'); }
          }}>Delete</button>
        {/if}
      </div>
    {/if}
  </div>
{/snippet}

{#snippet georgeRail()}
  {#if isDev}
    <div class="strat-devbar">
      ◆ chatMode (dev only)
      <div class="seg">
        {#each [['cro', 'Default'], ['george', $agentNameStore || 'George'], ['user', 'You']] as [val, label]}
          <button class:on={chatMode === val} onclick={() => chatMode = val as 'cro' | 'george' | 'user'}>{label}</button>
        {/each}
      </div>
    </div>
  {/if}
  <div class="rail-util">
    {#if messages.length > 0}
      <button type="button" class="rail-restart" onclick={restartConversation} title="Clear this conversation and start over">↺ Restart</button>
    {/if}
  </div>

  <div class="chat-messages">
    {#if messages.length === 0 && !chatLoading}
      <div class="rail-starter">
        <div class="rail-starter-av">✦</div>
        <div class="rail-starter-t">George is up to speed on {client.replace(/_/g, ' ')}</div>
        <div class="rail-starter-s">Ask a question below, or plan the next move.</div>
      </div>
    {/if}
    {#each messages as msg, i}
      {#if !msg.hidden}
        {#if msg.role === 'assistant'}
          <div class="gmsg">
            <div class="gmsg-head">
              <span class="g-avatar">✦</span>
              <span class="g-name">{$agentNameStore || 'George'}</span>
            </div>
            <div class="gmsg-body">
              <div class="md-content">{@html renderSafeMarkdown(msg.content)}</div>
            </div>
          </div>
        {:else if msg.role === 'system'}
          <div class="chat-msg system" style="align-self:center; background:transparent; border:none; box-shadow:none; max-width:100%; padding:4px 0;">
            <div style="font-size:11px; color:var(--text-dim); font-style:italic;">
              {#each msg.content.split('\n') as line}<div>{line}</div>{/each}
            </div>
          </div>
        {:else}
          <div class="chat-msg user">{msg.content}</div>
        {/if}
      {/if}
    {/each}
    {#if chatLoading}
      <div class="chat-msg assistant" style="display:flex; align-items:center; gap:8px;">
        <span class="spinner"></span> <span class="text-muted">Thinking...</span>
      </div>
    {/if}
  </div>

  <div class="ask-suggest">
    {#if !messages.some((m) => m.role === 'assistant')}
      <button class="sug-chip primary" onclick={() => quickAsk('Plan next steps')}>Plan next steps</button>
      <button class="sug-chip" onclick={() => quickAsk('Draft the follow-up')}>Draft the follow-up</button>
      <button class="sug-chip" onclick={() => quickAsk('What changed recently on this account?')}>What changed recently?</button>
    {:else}
      {#if !draftedThisSession}
        <button class="sug-chip primary" onclick={draftFromChat}>Draft email</button>
      {/if}
      <button class="sug-chip" disabled={savingInsights} onclick={handleSaveInsights}>{savingInsights ? 'Saving…' : 'Save to memory'}</button>
    {/if}
  </div>
  <div class="strat-ask">
    <textarea
      bind:value={chatInput}
      placeholder="Ask George, plan next steps, or draft an email…"
      class="flex-1"
      rows="2"
      style="resize: vertical; min-height: 2.4em; max-height: 12em; font-family: inherit;"
      onkeydown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          handleChat();
        }
      }}
    ></textarea>
    <button class="primary" onclick={handleChat} disabled={!chatInput.trim() && !chatLoading}>Send</button>
  </div>
{/snippet}

{#snippet composeArea()}
  <!-- ── Compose email — relocated from the George rail into the Emails segment ── -->
  <div class="compose-mb">
  {#if lastSentNotice}
    <div class="mt-3" style="background:color-mix(in srgb, var(--green) 10%, var(--bg-surface)); border:1px solid color-mix(in srgb, var(--green) 25%, var(--border)); border-radius:var(--radius); padding:8px 12px; font-size:13px; display:flex; justify-content:space-between; align-items:center;">
      <span>{lastSentNotice}</span>
      <button style="background:none; border:none; cursor:pointer; font-size:16px; color:var(--text-dim);" onclick={() => lastSentNotice = ''}>x</button>
    </div>
  {/if}
  <div class="compose-area mt-4">
    <div style="display:flex; justify-content:space-between; align-items:center;">
      <div class="compose-header"><span class="spark">✦</span> {chatDraftThread === '__new' ? 'New email' : 'Draft email'}</div>
      <button type="button" class="cx-btn" onclick={discardDraft} title="Discard draft" aria-label="Discard draft">×</button>
    </div>
    {#if chatDraftOpen}
      {#if chatDraftThread === '' || chatDraftThread === '__new'}
      <div class="mb-2">
        <label for="cv-chatDraftThread" class="text-xs text-dim">Thread</label>
        <select id="cv-chatDraftThread" bind:value={chatDraftThread}
                onchange={(e) => selectChatThread((e.target as HTMLSelectElement).value)}>
          <option value="">AI decides (recommended)</option>
          <option value="__new">New email</option>
          {#each emailThreads as t}
            <option value={t.stem}>Reply: {prettyThreadName(t)}</option>
          {/each}
        </select>
      </div>
      {/if}
      {#if personasData}
        <div class="mb-2">
          <label for="cv-selectedPersona" class="text-xs text-dim">Write as</label>
          <select id="cv-selectedPersona" bind:value={selectedPersona}
                    onchange={() => { if (selectedPersona) { setActivePersona(selectedPersona); viewStorage.setItem('selectedPersona', selectedPersona); } }}>
            {#each Object.entries(personasData.personas) as [key, p]}
              <option value={key}>{p.label || p.name}</option>
            {/each}
          </select>
        </div>
      {/if}
      <div class="row mb-3">
        <input bind:value={chatDraftInstruction} placeholder="Any extra instructions (optional)" class="flex-1" />
        <button class="primary" onclick={runGenerate} disabled={generating}>
          {#if generating}<span class="spinner"></span> Generating...{:else}Generate{/if}
        </button>
      </div>
      <div class="grid-2 mb-2">
        <div>
          <label for="cv-chatDraftTo" class="text-xs text-dim">TO</label>
          {#if clientEmails.length > 0}
            <select id="cv-chatDraftTo" bind:value={chatDraftTo}>
              <option value="">Select or type below...</option>
              {#each clientEmails as e}<option value={e}>{e}</option>{/each}
            </select>
          {/if}
          <input bind:value={chatDraftTo} placeholder="email@company.com" style="margin-top:4px;" />
        </div>
        <div>
          <label class="text-xs text-dim" for="cv-chatDraftSubject">
            Subject{#if chatDraftMeta.gmail_thread_id} <span style="opacity:0.7;">(locked to thread)</span>{/if}
          </label>
          <input id="cv-chatDraftSubject" bind:value={chatDraftSubject} placeholder="Subject"
                 readonly={!!chatDraftMeta.gmail_thread_id}
                 title={chatDraftMeta.gmail_thread_id ? 'Subject is locked while replying on an existing thread' : ''}
                 style={chatDraftMeta.gmail_thread_id ? 'background:var(--bg-disabled, rgba(0,0,0,0.04));cursor:not-allowed;' : ''} />
        </div>
      </div>
      <div class="grid-2 mb-2">
        <MultiSelect label="CC — Client contacts" options={clientEmails} bind:selected={chatDraftCcClient} />
        <MultiSelect label="CC — Your team" options={internalEmails} bind:selected={chatDraftCcInternal} />
      </div>
      <div class="mb-2">
        <label for="cv-chatDraftCcOther" class="text-xs text-dim">CC — Other (comma-separated)</label>
        <input id="cv-chatDraftCcOther" bind:value={chatDraftCcOther} placeholder="other@company.com" />
      </div>
      <div class="mb-3">
        <EmailBodyEditor bind:value={chatDraftBody} rows={10} placeholder="Email body..." />
      </div>
      {#if polishChanges}
        <div class="polish-changes mb-3">
          <div class="text-xs font-bold mb-1">Changes made:</div>
          <pre class="text-xs" style="white-space:pre-wrap;margin:0;font-family:inherit;">{polishChanges}</pre>
        </div>
      {/if}
      {#if chatDraftNotes}
        <div style="background:var(--bg-surface); border:1px solid var(--border); border-radius:var(--radius); padding:10px; margin-bottom:12px;">
          <div class="text-xs font-bold mb-1" style="color:var(--text-muted);">Strategy Notes</div>
          <pre class="text-xs" style="white-space:pre-wrap;margin:0;font-family:inherit;">{chatDraftNotes}</pre>
        </div>
      {/if}
      {@render attachmentWidget()}
      <!-- Same order + shared button vocabulary as Today's draft card:
           Send (coral) · secondary pill · ✦ tools (borderless) · drop (ghost, right). -->
      <div class="draft-actions">
        <button class="btn-primary" onclick={() => handleSend(
          chatDraftMeta.gmail_thread_id || '', chatDraftTo === '__manual' ? chatDraftToManual : chatDraftTo,
          chatDraftSubject, chatDraftBody,
          buildCc(chatDraftCcClient, chatDraftCcInternal, chatDraftCcOther)
        )} disabled={sending}>
          {#if sending}<span class="spinner"></span>{:else}Send{/if}
        </button>
        <div style="position:relative; display:inline-block;">
          <button class="btn-primary" onclick={() => scheduleOpen = !scheduleOpen}>Schedule</button>
          {#if scheduleOpen}
            <div style="position:absolute; bottom:100%; left:0; background:var(--bg-surface); border:1px solid var(--border); border-radius:var(--radius); padding:10px; z-index:10; min-width:200px; margin-bottom:4px;">
              <input type="date" bind:value={scheduleDate} onclick={openPicker} onfocus={openPicker} style="width:100%; margin-bottom:6px;" />
              <input type="time" bind:value={scheduleTime} onclick={openPicker} onfocus={openPicker} style="width:100%; margin-bottom:6px;" />
              <button class="btn-primary" style="width:100%;" onclick={() => handleScheduleSend(
                chatDraftMeta.gmail_thread_id || '', chatDraftTo === '__manual' ? chatDraftToManual : chatDraftTo,
                chatDraftSubject, chatDraftBody,
                buildCc(chatDraftCcClient, chatDraftCcInternal, chatDraftCcOther)
              )} disabled={sending}>Schedule Send</button>
            </div>
          {/if}
        </div>
        <button type="button" class="today-mini-btn" onclick={discardDraft}>Cancel</button>
        <span class="act-sep" aria-hidden="true"></span>
        {#each ['refine', 'polish'] as mode}
          <button class="today-mini-btn draft-ai-btn" onclick={async () => {
            if (!chatDraftBody.trim() || polishing) return;
            polishing = true; polishChanges = '';
            try {
              const r = await polishEmail({ client, body: chatDraftBody, subject: chatDraftSubject, mode: mode as 'polish' | 'refine' });
              chatDraftBody = r.body; chatDraftSubject = r.subject; polishChanges = r.changes;
            } catch (e) { polishChanges = `${mode} failed`; }
            polishing = false;
          }} disabled={!chatDraftBody.trim() || polishing}>
            <span class="draft-spark">✦</span> {polishing ? '...' : mode === 'polish' ? 'Polish wording' : 'Refine content'}
          </button>
        {/each}
        <button type="button" class="today-mini-btn draft-ai-btn" onclick={runGenerate} disabled={generating}>
          <span class="draft-spark">✦</span> {generating ? '...' : 'Regenerate'}
        </button>
      </div>
    {/if}
  </div>
  </div>
{/snippet}

{#snippet callDetail(cl: CallEntry)}
  {#if loadingCall}
    <div style="text-align:center; padding:16px;"><span class="spinner"></span></div>
  {:else if callContent}
    <div style="display:flex; gap:6px; margin-bottom:8px;">
      <button class="ghost-sm" onclick={() => { editingCall = !editingCall; if (editingCall) editCallText = callContent; }}>{editingCall ? 'Cancel' : 'Edit'}</button>
      <button class="ghost-sm" style="color:var(--red); border-color:color-mix(in srgb, var(--red) 40%, transparent);" onclick={async () => {
        if (!confirm('Delete this call record?')) return;
        await deleteCall(cl.client, callTypeFolder(cl.type), cl.filename);
        callEntries = callEntries.filter((e) => e.filename !== cl.filename);
        callContent = ''; expandedCall = '';
      }}>Delete</button>
    </div>
    {#if editingCall}
      <textarea bind:value={editCallText} rows="14" style="width:100%; font-family:var(--font-mono); font-size:12px; padding:8px; border:1px solid var(--border); border-radius:var(--radius); resize:vertical;"></textarea>
      <button class="ghost-sm" style="margin-top:8px; background:var(--accent); color:#fff; border-color:var(--accent);" onclick={async () => {
        await saveFileContent(cl.client, callTypeFolder(cl.type), cl.filename, editCallText);
        callContent = editCallText; editingCall = false;
      }}>Save</button>
    {:else}
      <div class="content-viewer md-content">{@html renderSafeMarkdown(callContent)}</div>
    {/if}
    {#if cl.has_transcript && cl.transcript_filename}
      <button class="ghost-sm" style="margin-top:12px;" onclick={() => toggleTranscript(cl)}>{showTranscript ? 'Hide transcript' : 'Show transcript'}</button>
      {#if showTranscript}
        {#if callTranscript}
          <div class="content-viewer md-content" style="margin-top:10px; max-height:340px; overflow-y:auto; background:var(--bg-surface); border:1px solid var(--border); border-radius:var(--radius); padding:12px;">{@html renderSafeMarkdown(callTranscript)}</div>
        {:else}
          <div style="text-align:center; padding:12px;"><span class="spinner"></span></div>
        {/if}
      {/if}
    {/if}
  {/if}
{/snippet}

{#snippet segPeople()}
  {#if !contactsReady || !callsReady || !emailsReady}
    <div class="segment-loading"><span class="spinner"></span> Loading contacts…</div>
  {:else if clientContacts.length === 0}
    <div class="empty-state">
      <div class="empty-state-icon">👥</div>
      <div class="empty-state-text">No contacts found for {client.replace(/_/g, ' ')}.</div>
    </div>
  {:else}
    {#each clientContacts as c}
      {@const fn = ((c.name || c.email || '').split(/[ @]/)[0] || '').toLowerCase()}
      {@const cThreads = fn ? files.filter((f) => ((f.summary || '') + ' ' + prettyThreadName(f)).toLowerCase().includes(fn)) : []}
      {@const cCalls = fn ? callEntries.filter((cl) => (((cl.preview || '') + ' ' + (cl.stem || '')).toLowerCase().includes(fn))) : []}
      <div class="crow" data-contact={c.email}>
        <div class="chead">
          <button type="button" class="et-toggle" onclick={() => expandedContact = expandedContact === c.email ? '' : c.email}>
            <span class="et-caret" class:open={expandedContact === c.email}>▸</span>
            <span class="ppl-av" style="background:{avatarColor(c.email)}">{initialsFor(c.name, c.email)}</span>
            <span class="cinfo">
              <span class="rname">{c.name || c.email.split('@')[0]}</span>
              <span class="rsub">{[c.title, c.org].filter(Boolean).join(', ')}</span>
            </span>
            <span class="cstat">{cThreads.length} email{cThreads.length === 1 ? '' : 's'} · {cCalls.length} call{cCalls.length === 1 ? '' : 's'}{c.last_seen ? ' · ' + daysAgo(c.last_seen) : ''}</span>
          </button>
          <span class="cacts">
            <button class="ghost-sm" onclick={() => writeToContact(c.email)}>Write</button>
            {#if c.phone}<a class="ghost-sm" href="tel:{c.phone}">Call</a>{/if}
          </span>
        </div>
        {#if expandedContact === c.email}
          <div class="cbody">
            <div class="cdetail"><span>{c.email}</span>{#if c.phone}<span>{c.phone}</span>{:else}<span class="cd-none">no phone on file</span>{/if}</div>
            {#each cThreads as f}
              <div class="ci">
                <span class="tlc">✉</span>
                <div class="tlbody">
                  <div class="tlhead"><span class="tlt">{prettyThreadName(f)}</span><span class="tld">{f.last_date ? fmtShortDate(f.last_date) : threadDate(f)}{f.has_inbound ? ' · inbound' : ''}</span></div>
                  {#if f.summary}<div class="tlx">{f.summary}</div>{/if}
                  <div class="tlacts"><button type="button" class="ghost-sm" onclick={() => openThreadInEmails(f)}>View in Emails →</button></div>
                </div>
              </div>
            {/each}
            {#each cCalls as cl}
              <div class="cic" class:open={expandedCall === cl.filename}>
                <button type="button" class="ci-h" onclick={() => expandCall(cl)}>
                  <span class="tlc">☎</span>
                  <div class="tlbody">
                    <div class="tlhead"><span class="tlt">{callTypeLabel(cl.type)}</span><span class="tld">{cl.date}</span></div>
                    {#if cl.preview}<div class="tlx">{cl.preview}</div>{/if}
                  </div>
                  <span class="ci-chev">{expandedCall === cl.filename ? '▲' : '▼'}</span>
                </button>
                {#if expandedCall === cl.filename}
                  <div class="ci-callbody">{@render callDetail(cl)}</div>
                {/if}
              </div>
            {/each}
            {#if cThreads.length === 0 && cCalls.length === 0}
              <div class="cd-none" style="padding:6px 0 2px;">No emails or calls matched to {c.name || 'this contact'} by name yet.</div>
            {/if}
          </div>
        {/if}
      </div>
    {/each}
  {/if}
{/snippet}

{#snippet segInsights()}
  <div class="insights-tab">
    <ScoreChart {client} />

    <div class="followup-section" style="margin-top:16px; padding:16px; background:var(--bg-surface); border:1px solid var(--border); border-radius:12px;">
      <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:8px;">
        <span style="font-size:13px; font-weight:600; color:var(--text);">Follow-up Date</span>
        {#if followupOverride}
          <button class="small" style="font-size:10px; padding:2px 6px; opacity:0.6; color:var(--text-muted);" onclick={handleClearFollowup}>Clear override</button>
        {/if}
      </div>
      <div style="display:flex; align-items:center; gap:8px;">
        <input
          type="date"
          bind:value={followupDate}
          style="width:150px; flex-shrink:0; background:var(--bg); border:1px solid var(--border-strong); border-radius:6px; padding:5px 8px; color:var(--text); font-size:12px;"
        />
        <input
          type="text"
          bind:value={followupReason}
          placeholder="Reason — optional (e.g. Bruce returns from leave)"
          style="flex:1; background:var(--bg); border:1px solid var(--border-strong); border-radius:6px; padding:5px 8px; color:var(--text); font-size:12px;"
        />
        <button class="small primary" style="font-size:11px; padding:5px 12px;" onclick={handleSetFollowup}>Set</button>
      </div>
      {#if followupOverride}
        <div style="margin-top:6px; font-size:11px; color:var(--green);">
          Manual override: {followupOverride.date} — {followupOverride.reason || 'no reason'}
        </div>
      {/if}
    </div>
  </div>
{/snippet}

{#if toast}
  <div class="toast" class:toast-success={!toastError} class:toast-error={toastError}>{toast}</div>
{/if}

<style>
  /* ── Client header — title + subtle rebuild affordance ── */
  .client-header-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
  .client-header {
    display: flex;
    /* NOT baseline: the pill's text baseline would sit on the title's, leaving its padding
       and border-radius hanging below a 24px cap-height — it reads as dropped. */
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    /* This owns the space below the header, not `.page-title` — as a flex item the title's
       own margin-bottom would make its box taller and recentre the pill low. Change one,
       change the `.page-title { margin-bottom: 0 }` below it. */
    margin-bottom: 6px;
  }
  .client-header .page-title { margin-bottom: 0; }
  /* Optical, not geometric: "Barrick Gold" has no descenders, so the title's ink sits high
     in its line box and a geometrically centred pill reads 2px high. This is the one dial —
     change it here, not by touching align-items. */
  .client-header .et-badge { position: relative; top: 2px; }
  .segment-loading { display: flex; align-items: center; gap: 8px; padding: 24px 12px; color: var(--text-dim); font-size: 13px; }
  .client-stage-pill {
    position: relative;
    top: 2px;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 2px 6px;
    border: 1px solid;
    border-radius: 4px;
    background: transparent;
  }
  /* The email thread accordion vocabulary (`.ethread`, `.et-*`, `.emsg`, `.em-*`,
     `.esearch`, `.btn-newemail`) now lives in app.css — the Fund LP page writes the same
     class names and was getting none of them while these were component-scoped. Only
     the rules that are deliberately ClientView-only stay here. */
  /* Follow-up status pill — same shape/colors as the Dashboard status column. */
  .et-badge { display: inline-flex; padding: 3px 9px; border-radius: 12px; font-size: 10.5px; font-weight: 500; white-space: nowrap; flex-shrink: 0; }
  /* The page flows now, so nothing inside the records column keeps a scrollbar of its
     own. The global .content-viewer caps at 65vh + overflow-y:auto (app.css), which
     nested a THIRD scrollbar inside an expanded Memory note or call summary. Scoped
     off here only — every other page keeps the global behaviour. (The raw transcript
     box keeps its inline 340px cap: that's an opt-in dump, not a reading surface.) */
  .et-body .content-viewer,
  .ci-callbody .content-viewer { max-height: none; overflow: visible; }
  /* Emails header: just the coral "+ New email" (pages enter straight into content, no title) */
  .drafts-head { display: flex; align-items: center; justify-content: flex-end; margin-bottom: 14px; }
  .cx-btn { background: none; border: none; color: var(--text-dim); font-size: 18px; line-height: 1; cursor: pointer; padding: 0 4px; }
  .cx-btn:hover { color: var(--text); }

  @media (max-width: 700px) {
    .compose-area .row { flex-wrap: wrap; gap: 6px; }
    .compose-area button { font-size: 12px; padding: 6px 10px; }
  }

  .rebuild-check {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    text-align: left;
    gap: 8px;
    font-size: 12px;
    color: var(--text, #e0e0e0);
    cursor: pointer;
    padding: 3px 0;
  }
  .rebuild-check input {
    accent-color: var(--blue, #60a5fa);
    margin: 0;
    flex-shrink: 0;
    /* Nudge the checkbox to align with the first line of text when text wraps. */
    margin-top: 2px;
  }

  /* ─── Emails-tab maintenance footer (quiet, scrolled-to position) ─── */
  .rebuild-footer {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 1px dashed var(--border);
    display: flex;
    justify-content: center;
  }
  .rebuild-footer-trigger {
    background: transparent;
    border: none;
    color: var(--text-dim);
    font-size: 11px;
    padding: 4px 8px;
    cursor: pointer;
    letter-spacing: 0.02em;
  }
  .rebuild-footer-trigger:hover:not(:disabled) { color: var(--text-muted); }
  .rebuild-footer-trigger:disabled { cursor: default; opacity: 0.7; }
  .rebuild-panel {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 14px 16px;
    width: 100%;
    max-width: 360px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }
  .rebuild-panel-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 8px;
  }
  .rebuild-panel-confirm .rebuild-panel-title { color: var(--red, #e74c3c); }
  .rebuild-confirm-copy {
    font-size: 12px;
    line-height: 1.5;
    color: var(--text-muted);
    margin: 0 0 12px 0;
  }
  .rebuild-panel-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 10px;
  }
  .rebuild-panel-actions .small { font-size: 11px; padding: 5px 12px; }
  .rebuild-panel-actions .small.danger {
    background: var(--red, #e74c3c);
    color: white;
    border-color: var(--red, #e74c3c);
  }
  .rebuild-panel-actions .small.danger:hover { opacity: 0.9; }

  /* ═══════════════ Strategize tab ═══════════════ */
  /* Coral = --accent (action/brand). New SCOPED classes so the global .chat-msg /
     .md-content used by other pages/tabs are left untouched. */

  /* dev-only chatMode toggle (user-facing control removed; George/You kept for dev) */


  /* `.spark` is global (app.css); `.tab-assist .spark` (app.css) layers a margin on top. */




  /* Compose action row now uses the SHARED draft-editor vocabulary in app.css
     (.btn-primary / .today-mini-btn / .draft-ai-btn / .draft-spark) — same order +
     classes as Today's draft card. The old borderless `.ai-act` tool + the
     `.draft-actions button` type-pin were removed with that switch. */

  /* The composer's label + field treatment lives in app.css beside `.compose-area`, so
     every composer shares it. It outranks the global `input:not(...)` reset there —
     (0,3,1) vs (0,2,1), the `.compose-area` class being the margin — and being global it
     also reaches labels rendered by child components, which is what keeps MultiSelect's
     "CC — …" labels the same size as the TO/Subject ones. */

  /* This page's chrome above `.split-shell`: 36px `.main-content` pad-top + ~70px of
     back-crumb + page title + margin, plus ~12px slack. The shared rule in app.css
     deliberately sets no default — re-measure this if the header block changes height. */
  .cv-shell { --split-chrome: 118px; }

  /* The tab bar pins to the top of the page scroller. This works ONLY because nothing
     between here and .main is a scroll container or clips: .main-content is
     `overflow-x: clip` on purpose (app.css:663) and .split-shell / .split-main deliberately
     set no overflow. z-index 5 sits below MultiSelect's dropdown (50) and the Schedule
     popover (10), so both still open over the bar. */
  .cv-main .tabs {
    position: sticky;
    top: 0;
    z-index: 5;
    background: var(--bg);
    margin-bottom: 0;
  }
  /* No bottom padding — .main already closes the page with 28px. No right padding —
     the pane no longer scrolls, so there is no scrollbar gutter to clear (that 12px
     was quietly offsetting the records column from its own tab bar). */
  .cv-mainbody { padding: 18px 0 0; }




  /* People / contacts segment (reuses Contact records + avatar helpers). */
  /* Contacts — expandable rows with a per-contact email/call timeline (best-effort name match) */
  .ppl-av {
    width: 34px; height: 34px; border-radius: 50%; color: #fff; flex-shrink: 0;
    font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center;
  }
  .crow { border: 1px solid var(--border); border-radius: 10px; margin-bottom: 10px; overflow: hidden; background: var(--bg-surface); }
  .chead { display: flex; align-items: center; gap: 10px; padding-right: 14px; }
  .cinfo { display: flex; flex-direction: column; gap: 1px; min-width: 0; text-align: left; }
  .rname { font-size: 13.5px; font-weight: 600; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .rsub { font-size: 11.5px; color: var(--text-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cstat { font-family: var(--font-mono); font-size: 10.5px; color: var(--text-dim); white-space: nowrap; margin-left: auto; }
  .cacts { display: flex; gap: 6px; flex-shrink: 0; }
  .cbody { padding: 2px 15px 14px; border-top: 1px solid var(--border); }
  .cdetail { display: flex; gap: 16px; flex-wrap: wrap; padding: 11px 0 12px; border-bottom: 1px solid var(--border); font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); }
  .cd-none { color: var(--text-dim); }
  .ci { display: flex; gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--border); }
  .ci:last-child { border-bottom: none; }
  .cic { border-bottom: 1px solid var(--border); }
  .cic:last-child { border-bottom: none; }
  .ci-h { display: flex; gap: 12px; padding: 12px 0; width: 100%; background: none; border: none; text-align: left; font: inherit; color: inherit; cursor: pointer; align-items: flex-start; }
  .ci-chev { margin-left: auto; font-size: 9px; color: var(--text-dim); align-self: center; flex-shrink: 0; }
  .ci-callbody { padding: 0 0 12px 38px; }
  .tlc { width: 26px; height: 26px; border-radius: 7px; background: var(--bg-hover); border: 1px solid var(--border); color: var(--text-muted); font-size: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .tlbody { flex: 1; min-width: 0; }
  .tlhead { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; margin-bottom: 3px; }
  .tlt { font-size: 13px; font-weight: 600; }
  .tld { font-family: var(--font-mono); font-size: 10.5px; color: var(--text-dim); margin-left: auto; white-space: nowrap; }
  .tlx { font-size: 12.5px; color: var(--text-muted); line-height: 1.5; }
  .tlacts { margin-top: 8px; }
  /* `font-weight` because this class is worn by both <button> and <a> — see app.css's note
     on the global `button` rule; without it the links render 400 beside 500 buttons. */
  .ghost-sm {
    display: inline-flex; align-items: center; text-decoration: none;
    background: transparent; border: 1px solid var(--border); color: var(--text-muted);
    border-radius: 7px; padding: 4px 11px; font-size: 11.5px; font-weight: 500;
    cursor: pointer; font-family: inherit;
  }
  .ghost-sm:hover { color: var(--text); border-color: var(--text-dim); }
  /* (per-contact timeline covers calls; the old flat "All calls" section was removed) */
</style>
